/**
 * The English dictionary — the source of truth for every other language.
 *
 * `Dictionary` is derived from this object with `typeof`, and every other locale file
 * is annotated `: Dictionary`. That makes an incomplete translation a **compile
 * error** rather than a page that silently falls back to English half way down, which
 * is the failure mode that makes localized sites feel broken.
 *
 * Keys are grouped by the surface they appear on. Anything with `{placeholder}` is
 * interpolated by `t()` — keep the placeholder names identical across locales.
 */
const en = {
  // ---- /citation-styles/alphabetical/<letter> ----
  styleDirectory: {
    notFoundTitle: "Citation style directory not found",
    metaTitle: "Citation Styles Beginning with {label}",
    metaDescription:
      "Browse {count} Citation Style Language styles beginning with {label}. Open any style for real citation examples and a free generator.",
    ogSubtitle: "{count} styles with dedicated examples and generators.",
    ogKind: "A–Z Directory",
    schemaName: "Citation styles beginning with {label}",
    schemaDescription:
      "An alphabetical directory of {count} Citation Style Language styles beginning with {label}.",
    breadcrumbStyles: "Citation styles",
    heading: "Citation Styles Beginning with {label}",
    intro:
      "Browse all {count} styles in this part of the CSL catalog. Each page includes the style’s own formatted examples and a free citation generator.",
    navLabel: "Citation styles A to Z",
    listHeading: "All styles beginning with {label}",
  },
  nav: {
    tools: "Tools",
    guides: "Guides",
    styles: "All Styles",
    bibliography: "My Bibliography",
    search: "Search",
    glossary: "Glossary",
    home: "Home",
    menu: "Menu",
  },

  home: {
    title: "Free Citation Generator",
    subtitle: "Updated for 2026",
    intro:
      "Paste a DOI, URL, or BibTeX — or search for a book — and get a formatted citation in APA, MLA, Chicago, Vancouver, Harvard, and thousands more styles. 100% free, no paid subscription, no sign-up.",
    browseAllStyles: "Browse all {count} citation styles →",
    featuresHeading: "Everything You Need to Cite",
    featureStyles: "{count} styles",
    featureStylesDetail: "APA, MLA, Chicago and more",
    featureBibliographies: "Multiple bibliographies",
    featureBibliographiesDetail: "Organize by project",
    featureBookSearch: "Book search",
    featureBookSearchDetail: "Find by title or author",
    featureExport: "Export anywhere",
    featureExportDetail: "BibTeX, Word, RIS, CSV",
    featureDarkMode: "Dark mode",
    featureDarkModeDetail: "Easy on the eyes",
    featureNoSignup: "No sign-up",
    featureNoSignupDetail: "Free forever, no paid subscription",
    aboutHeading: "A free citation generator for students and researchers",
    aboutBody:
      "CitationEasy supports over 10,000 academic citation styles. Whether you need APA for psychology, MLA for literature, Chicago for history, or Vancouver for medical journals, the formatting comes from the style's own official definition — not from an approximation.",
    howItWorksHeading: "How it works",
    howItWorksStep1:
      "Paste a DOI, URL, ISBN, PubMed ID, arXiv ID, or BibTeX entry — or search for a book by title.",
    howItWorksStep2:
      "Choose your citation style. Type to search all {count} of them.",
    howItWorksStep3:
      "Copy the finished reference, or save it to a bibliography and export to Word, BibTeX, or RIS.",
    privacyNote:
      "Your bibliography is stored in your own browser. No account, and nothing is uploaded.",
  },

  hubs: {
    toolsTitle: "Free writing and citation tools",
    toolsIntro:
      "{count} browser-based tools for writing and citing: generators for every kind of source, word and character counters, readability scoring, formatters, and converters. All free, all instant.",
    stylesTitle: "Browse every citation style",
    stylesIntro:
      "All {count} styles from the Citation Style Language project, searchable and filterable by country, subject, and academic field.",
    guidesTitle: "Citation and academic writing guides",
    guidesIntro:
      "In-depth guides on citing specific kinds of source, evaluating what you find, and writing well.",
    howToCiteTitle: "How to cite any source",
    howToCiteIntro:
      "Step-by-step pages pairing every common source type with the major citation styles, each with a real worked example.",
    faqTitle: "Citation questions and answers",
    faqIntro:
      "Short, accurate answers to the questions students actually ask about citing.",
    glossaryTitle: "Citation glossary",
    glossaryIntro:
      "Plain-language definitions of the terms that show up in style guides and assignment briefs.",
    openHub: "Open",
  },

  styleLanding: {
    metaTitle: "Free {style} Citation Generator",
    metaDescription:
      "Generate {style} citations free. Paste a DOI, URL, ISBN, or BibTeX and get a correctly formatted {style} reference and in-text citation.",
    heading: "{style} Citation Generator",
    intro:
      "Create accurate {style} citations for books, journal articles, and websites in one click. Free, with no sign-up.",
    aboutHeading: "About {style}",
    whenToUseHeading: "When to use {style}",
    factsHeading: "{style} at a glance",
    factEdition: "Edition",
    factSystem: "Citation system",
    factInText: "In-text format",
    factReferenceList: "List name",
    factFields: "Common in",
    systemAuthorDate: "Author–date",
    systemNumbered: "Numbered",
    systemFootnotes: "Footnotes",
    howToHeading: "How to use this {style} generator",
    howToStep1:
      "Paste a URL, DOI, ISBN, or BibTeX entry into the box above — or search for a book by title.",
    howToStep2:
      "{style} is already selected on this page, so you can go straight to generating.",
    howToStep3:
      "Copy the result into your document, or save it and export the whole list later.",
    relatedHeading: "More {style} resources",
    englishOnlyNote:
      "Detailed {style} guides and per-source examples are available in English.",
  },

  // The per-style pages at `/<locale>/citation-style/<slug>` — one per catalog style.
  //
  // The formatted examples the page is built around (the style's own output for a
  // fixed journal article, book and website) and the style's own name stay in
  // `src/data/citation-styles.ts` and `src/data/style-examples-generated.json`. They
  // are language-neutral: a citation rendered by the style's own CSL definition looks
  // the same whatever language the reader speaks, and "translating" one would make the
  // page wrong.
  //
  // The rest of what `src/data/**` supplies to these pages is NOT like that — see the
  // `generic*`, `discipline*` and `internationalLabel` keys below.
  citationStylePage: {
    notFoundTitle: "Citation style not found",
    metaTitle: "{style} Citation Generator - Free {style} Format",
    metaFormatAndFields: "{style} is a {format} style used in {fields}.",
    metaFormatOnly: "{style} is a {format} style.",
    metaFieldsOnly: "{style} is used in {fields}.",
    metaPromiseNote: "See a real {style} footnote and generate your own free.",
    metaPromiseExamples:
      "See real {style} citation examples for a journal article, book, and website, and generate your own free.",
    ogKindFormat: "{format} style",
    ogKindDefault: "Citation style",
    // Joins the style's discipline list: "psychology, education and nursing".
    disciplineConjunction: "and",

    // ---- Data-derived facts that are nonetheless mail-merged prose ----
    //
    // Every entry in `src/data/citation-styles-generated.ts` carries exactly the
    // two sentences below with their own name spliced in, exactly one region value
    // ("International" — that is `internationalLabel` above, reused rather than
    // duplicated), and discipline tokens drawn from a closed 26-word vocabulary. None
    // of that is a citation, so none of it is language-neutral the way the formatted
    // examples are. Left in `src/data/**` it renders English facts inside translated
    // chrome, which is precisely the hybrid page this tree exists to avoid.
    //
    // The page substitutes the two templates ONLY when a style's stored text is
    // byte-identical to the English template with its own name interpolated, so the
    // ~100 hand-curated styles keep their bespoke English descriptions instead of
    // being flattened into a generic sentence. `{name}` is the style's full name.
    genericDescription:
      "Citation style for {name}. From the Citation Style Language repository.",
    genericWhenUsed:
      "When submitting to or citing in publications that use the {name} style.",

    // The 26 discipline tokens `style-examples-generated.json` uses. The page used to
    // print the raw token with hyphens turned into spaces, which read fine for
    // `generic-base` and badly for the two underscored ones — 920 pages said
    // "political_science" and "social_science" to readers. Naming them here is what
    // made that fixable; an unknown token (a new one from upstream CSL) still falls
    // back to the prettified form rather than to a key name.
    disciplineAnthropology: "anthropology",
    disciplineAstronomy: "astronomy",
    disciplineBiology: "biology",
    disciplineBotany: "botany",
    disciplineChemistry: "chemistry",
    disciplineCommunications: "communications",
    disciplineEngineering: "engineering",
    disciplineGenericBase: "generic base",
    disciplineGeography: "geography",
    disciplineGeology: "geology",
    disciplineHistory: "history",
    disciplineHumanities: "humanities",
    disciplineLaw: "law",
    disciplineLinguistics: "linguistics",
    disciplineLiterature: "literature",
    disciplineMath: "math",
    disciplineMedicine: "medicine",
    disciplinePhilosophy: "philosophy",
    disciplinePhysics: "physics",
    disciplinePoliticalScience: "political science",
    disciplinePsychology: "psychology",
    disciplineScience: "science",
    disciplineSocialScience: "social science",
    disciplineSociology: "sociology",
    disciplineTheology: "theology",
    disciplineZoology: "zoology",

    // Plain-language descriptions of the citation-format classes the CSL definitions
    // declare. `{format}` in the fallback is the raw class name from the data.
    formatClassAuthorDate:
      "an author–date style: in-text citations name the author and year, and the reference list is alphabetized by author",
    formatClassNumeric:
      "a numeric style: sources are numbered in the order they are first cited, and in-text citations use those numbers",
    formatClassNote:
      "a note-based style: citations appear in footnotes or endnotes rather than parentheses in the text",
    formatClassLabel:
      "a label style: in-text citations use a compact label built from the author name and year",
    formatClassAuthor:
      "an author-only style: in-text citations name the author, with full details in the reference list",
    formatClassOther: "a {format} style",
    breadcrumbStyles: "Citation styles",
    internationalLabel: "International",
    heading: "{style} Citation Generator",
    intro:
      "Create accurate {style} citations for books, articles, and websites in one click.",
    examplesHeading: "{style} citation examples",
    examplesIntro: "The same three sources formatted in {style}:",
    examplesIntroWithFormat:
      "The same three sources formatted in {style} — {format}:",
    examplesNoteOnlyIntro:
      "{style} is a note-based style: it has no reference list, and every citation is a footnote. Here is our sample journal article cited in {style}:",
    labelJournalArticle: "Journal article",
    labelBook: "Book",
    labelWebsite: "Website",
    labelFootnote: "Footnote (journal article)",
    labelInText: "In-text citation (for the journal article)",
    aboutHeading: "About {style}",
    whenToUseLabel: "When to use {style}:",
    commonInLabel: "Common in:",
    subjectAreasLabel: "Subject areas:",
    formattingRulesLabel: "Formatting rules:",
    parentStylePrefix: "{style} follows the formatting of",
    parentStyleSuffix: ", the parent style it is based on.",
    howToHeading: "How to use this {style} generator",
    howToBody:
      'Paste a DOI, a URL to a news article or webpage, or raw BibTeX into the box above. You can also search for a book by title or author and cite it by ISBN. Choose "{style}" in the style dropdown (it\'s pre-selected on this page), then click "Generate citation." Copy the result into your reference list or bibliography. You can edit the generated entry (e.g. fix a title or add a date) before copying.',
    faqHeading: "Frequently asked questions about {style}",
    faqWhatIsQ: "What is {style}?",
    faqWhenQ: "When should I use {style}?",
    faqInTextQ: "What does an in-text citation look like in {style}?",
    faqInTextA:
      "{style} is {format}. Citing our sample journal article in the text looks like: {example}",
    faqReferenceQ: "What does a {style} reference list entry look like?",
    faqReferenceA:
      "A journal article formatted in {style} looks like this: {example}",
    faqWhereQ: "Where is {style} commonly used?",
    faqWhereA: "{style} is used {region}. {whenUsed}",
    faqRegionDefault: "internationally",
    faqGenerateQ: "How can I generate a {style} citation?",
    faqGenerateA:
      "Use the free generator on this page: paste a DOI, URL (e.g. a news article), or BibTeX, or search for a book by title or author. Select {style} as the citation style and click Generate to get a formatted reference you can copy into your paper or bibliography.",
    footerSupports:
      "supports many citation styles from around the world. Our generator uses Citation Style Language (CSL) styles from the",
    footerRepoLink: "official CSL repository",
  },

  // The source × style pages at `/<locale>/how-to-cite/<slug>` — 1,290 combinations.
  //
  // Same division as above: the citation templates, the worked examples generated from
  // each style's own CSL definition, the in-text samples and the hand-checked
  // formatting rules stay in the page module and in
  // `src/data/how-to-cite-examples-generated.json`. They are the citations themselves,
  // not prose about them. Source names, style names and editions come from
  // `src/data/how-to-cite.ts` and arrive here as placeholder values.
  // The links that join a how-to-cite page, its generator, and its guide, and the
  // lists of how-to-cite pages on tools, guides, and style hubs (src/data/cross-links.ts).
  crossLinks: {
    generateHeading: "Generate this citation",
    generateBody:
      "Paste a URL, DOI, ISBN, or the source details and get a finished {style} citation for {source}.",
    generateCta: "Open the {tool} →",
    guideHeading: "Go deeper",
    guideCta: "Read the full guide: {guide}",
    howToCiteHeading: "How to cite {source} in each style",
    howToCiteBody:
      "Format rules, templates, and worked examples for the styles most often required.",
    howToCiteMore: "All source types and styles →",
    citeNowHeading: "Cite {source} in {style} now",
    citeNowHowTo: "Format rules and worked examples",
    citeNowTool: "Generate it with the {tool}",
    citeNowHub: "{style} citation generator",
    hubSourcesHeading: "How to cite common sources in {style}",
    hubGuidesHeading: "{style} guides",
    hubCompareHeading: "Compare {style} with other styles",
    // "More in Statistics Calculators" under a tool: its next siblings on the /tools shelf.
    moreInCollection: "More in {collection}",
    // The link under that block to the shelf's own page, /tools/collections/<slug>.
    seeAllInCollection: "See all {collection}",
  },

  howToCitePage: {
    notFoundTitle: "Not Found",
    title: "How to Cite {source} in {style} ({edition})",
    metaTitle: "How to Cite {source} in {style} [{edition}, Updated 2026]",
    metaDescription:
      "Learn how to cite {description} in {style} format ({edition}). Free examples, templates, and in-text citation guides.",
    keywordCite: "cite {source} {style}",
    keywordCitation: "{style} {source} citation",
    keywordHowTo: "how to cite {source}",
    keywordExample: "{style} citation example",
    keywordReference: "{source} reference {style}",
    // What each style calls the list at the back of the paper, in the two casings the
    // prose needs. MLA says "Works Cited", Chicago and the legal styles "Bibliography".
    refListWorksCited: "Works Cited",
    refListWorksCitedPage: "Works Cited page",
    refListBibliography: "Bibliography",
    refListBibliographyLower: "bibliography",
    refListReferenceList: "Reference List",
    refListReferenceListLower: "reference list",
    backLink: "← All Citation Guides",
    lead: "Learn how to cite {description} in {style} format ({edition}). This guide includes the reference list format, in-text citation examples, and key formatting rules.",
    referenceFormatHeading: "{style} {list} Format",
    referenceFormatIntro:
      "Here is the standard {style} format for citing {source} in your {list}:",
    noTemplateNote:
      "Follow the standard {style} template for this source type.",
    workedHeading: "{style} Example for {source}",
    workedIntro:
      "A complete {style} reference for {source}, formatted by the official {style} CSL style definition — the same engine that powers the generator on this site:",
    workedNoteOnlyIntro:
      "{style} does not give {source} an entry in the {list} — it is cited in a note only. Here is that note, formatted by the official {style} CSL style definition:",
    workedOscolaNote:
      "The OSCOLA output generated on this site follows the 4th-edition CSL style, because no 5th-edition CSL style has been published in the Citation Style Language repository yet. Adjust it by hand: replace any ibid with the short name and (n X), use the 5th-edition court identifiers, put an Act's short form in single quotation marks, and prefer a persistent link or DOI to an access date",
    // The same two intros for the focused source types (`FOCUSED_SOURCE_TYPES`), whose
    // examples are written by hand rather than generated from a CSL definition.
    workedCheckedIntro:
      "A complete {style} reference for {source}, written by hand and checked against the official {style} guidance for this source:",
    workedCheckedNoteOnlyIntro:
      "{style} does not give {source} an entry in the {list} — it is cited in a note only. Here is that note, written by hand and checked against the official {style} guidance:",
    workedInTextLabel: "Matching in-text citation:",
    inTextHeading: "{style} In-Text Citation",
    inTextIntro:
      "Use one of these formats when referencing this source within your paper:",
    inTextParenthetical: "Parenthetical",
    inTextNarrative: "Narrative",
    rulesHeading: "Key {style} Formatting Rules",
    stepsHeading: "Step-by-Step Guide",
    step1Label: "Gather source information:",
    step1Body:
      "Collect the author name, title, publication date, and URL or DOI for your {source}.",
    step2Label: "Format the reference:",
    step2Body:
      "Arrange the elements following the {style} ({edition}) template shown above.",
    step3Label: "Create the in-text citation:",
    step3Body:
      "Add a parenthetical or narrative citation in your paper where you reference this source.",
    step4Label: "Add to your {list}:",
    step4Body: "Include the full formatted citation at the end of your paper.",
    step5Label: "Double-check formatting:",
    step5Body:
      "Verify italics, punctuation, capitalization, and hanging indents match {style} requirements.",
    ctaHeading: "Generate This Citation Automatically",
    ctaBody:
      "Skip the manual formatting. Paste a DOI, URL, or ISBN into CitationEasy and get a perfectly formatted {style} citation in seconds.",
    ctaButton: "Cite in {style} Now →",
    otherStylesHeading: "Cite {source} in Other Styles",
    comboLink: "{source} in {style}",
    otherSourcesHeading: "Cite Other Sources in {style}",
    resourcesHeading: "More Resources",
    resourceGenerator: "Free Citation Generator ({count} styles)",
    resourceTools: "Free Writing Tools",
    resourceCompare: "Compare Citation Styles",
    resourceGuides: "All Citation Guides",
    resourceCheatSheets: "Cheat Sheets (APA, MLA, Chicago)",
    resourceGlossary: "Citation Glossary (et al., ibid., DOI, hanging indent)",
    // HowTo structured data. Deliberately worded differently from the visible steps
    // above: the schema describes the task generically, the page walks the reader
    // through this particular source and style.
    schemaStep1Name: "Gather source information",
    schemaStep1Text:
      "Collect all required information for your {source} including author, title, date, and URL or DOI.",
    schemaStep2Name: "Format in {style}",
    schemaStep2Text:
      "Follow the {style} ({edition}) formatting rules to arrange the citation elements in the correct order.",
    schemaStep3Name: "Create the in-text citation",
    schemaStep3Text:
      "Add a corresponding in-text citation in your paper using the {style} format.",
    schemaStep4Name: "Add to reference list",
    schemaStep4Text:
      "Include the full citation in your {list} at the end of your paper.",
    faqHowQ: "How do I cite {source} in {style}?",
    faqHowA:
      "To cite {source} in {style} ({edition}), include the author, title, date, and source location. Use our free citation generator for automatic formatting.",
    faqFormatQ: "What is the {style} format for {source} citation?",
    faqFormatA: "The {style} format for {source} is: {example}",
    faqFormatFallback:
      "The {style} format requires specific elements like author, title, date, and source location arranged in a particular order.",
    faqInTextQ:
      "What does the in-text citation look like for {source} in {style}?",
    faqInTextA:
      "In {style}, the in-text citation for the example above is {example}",
  },

  // The 43 source types the how-to-cite pages are built from (`SOURCE_TYPES` in
  // `src/data/how-to-cite.ts`), in the five forms those pages print. Each form is a whole
  // phrase, never an article and a noun joined in code: an article agrees with its noun
  // in gender, number and case, elides in French and Italian, and has no counterpart
  // at all in Polish, Russian or Japanese.
  //
  // - `…Name` ("Book") is the type on its own, as a label: `howToCitePage.comboLink`.
  //   English also lower-cases it into the `keyword*` templates.
  // - `…Title` ("a Book") is the type as the object of "cite" in a title or heading:
  //   `title`, `metaTitle`, `workedHeading` and `otherStylesHeading`, plus
  //   `crossLinks.generateBody`, `howToCiteHeading` and `citeNowHeading`. Through
  //   `title` it is also the HowTo name, the OG card and every link to the page.
  // - `…Phrase` ("a book") is the same inside a sentence: `referenceFormatIntro`,
  //   `workedIntro`, `workedNoteOnlyIntro` and the five `faq*` questions and answers.
  // - `…Noun` ("book") is the bare noun inside a sentence, where English puts "your" in
  //   front of it: `step1Body` and `schemaStep1Text`. A translation also uses it in the
  //   `keyword*` templates, which read as search queries.
  // - `…Description` is what the type covers: `{description}` in `lead` and
  //   `metaDescription`.
  //
  // A language with case picks one case per form and words every template above around
  // it — German writes Title and Phrase in the accusative, Polish and Russian write Name
  // and Noun in the nominative. Changing a form's case means re-reading every template
  // that receives it. A key missing here falls back to the English data
  // (`howToCiteSourceForms`), so a new source type renders in English, not as a key.
  howToCiteSources: {
    websiteName: "Website",
    websiteTitle: "a Website",
    websitePhrase: "a website",
    websiteNoun: "website",
    websiteDescription: "webpages, online articles, and web content",

    bookName: "Book",
    bookTitle: "a Book",
    bookPhrase: "a book",
    bookNoun: "book",
    bookDescription: "printed and electronic books",

    journalArticleName: "Journal Article",
    journalArticleTitle: "a Journal Article",
    journalArticlePhrase: "a journal article",
    journalArticleNoun: "journal article",
    journalArticleDescription: "peer-reviewed and academic journal articles",

    youtubeVideoName: "YouTube Video",
    youtubeVideoTitle: "a YouTube Video",
    youtubeVideoPhrase: "a YouTube video",
    youtubeVideoNoun: "YouTube video",
    youtubeVideoDescription: "YouTube and online videos",

    podcastName: "Podcast",
    podcastTitle: "a Podcast",
    podcastPhrase: "a podcast",
    podcastNoun: "podcast",
    podcastDescription: "podcast episodes and series",

    newspaperArticleName: "Newspaper Article",
    newspaperArticleTitle: "a Newspaper Article",
    newspaperArticlePhrase: "a newspaper article",
    newspaperArticleNoun: "newspaper article",
    newspaperArticleDescription: "print and online newspaper articles",

    magazineArticleName: "Magazine Article",
    magazineArticleTitle: "a Magazine Article",
    magazineArticlePhrase: "a magazine article",
    magazineArticleNoun: "magazine article",
    magazineArticleDescription: "print and online magazine articles",

    blogPostName: "Blog Post",
    blogPostTitle: "a Blog Post",
    blogPostPhrase: "a blog post",
    blogPostNoun: "blog post",
    blogPostDescription: "blog posts and online opinion pieces",

    wikipediaName: "Wikipedia",
    wikipediaTitle: "Wikipedia",
    wikipediaPhrase: "Wikipedia",
    wikipediaNoun: "Wikipedia",
    wikipediaDescription: "Wikipedia articles and wiki entries",

    pdfName: "PDF",
    pdfTitle: "a PDF",
    pdfPhrase: "a PDF",
    pdfNoun: "PDF",
    pdfDescription: "PDF documents and reports",

    interviewName: "Interview",
    interviewTitle: "a Interview",
    interviewPhrase: "a interview",
    interviewNoun: "interview",
    interviewDescription: "personal, published, and broadcast interviews",

    socialMediaName: "Social Media Post",
    socialMediaTitle: "a Social Media Post",
    socialMediaPhrase: "a social media post",
    socialMediaNoun: "social media post",
    socialMediaDescription:
      "tweets, Instagram posts, Facebook posts, and TikToks",

    governmentDocumentName: "Government Document",
    governmentDocumentTitle: "a Government Document",
    governmentDocumentPhrase: "a government document",
    governmentDocumentNoun: "government document",
    governmentDocumentDescription:
      "government reports, legislation, and official documents",

    dissertationName: "Dissertation or Thesis",
    dissertationTitle: "a Dissertation or Thesis",
    dissertationPhrase: "a dissertation or thesis",
    dissertationNoun: "dissertation or thesis",
    dissertationDescription: "doctoral dissertations and master's theses",

    conferencePaperName: "Conference Paper",
    conferencePaperTitle: "a Conference Paper",
    conferencePaperPhrase: "a conference paper",
    conferencePaperNoun: "conference paper",
    conferencePaperDescription: "conference proceedings and presentations",

    lectureNotesName: "Lecture or PowerPoint",
    lectureNotesTitle: "a Lecture or PowerPoint",
    lectureNotesPhrase: "a lecture or powerpoint",
    lectureNotesNoun: "lecture or powerpoint",
    lectureNotesDescription: "class lectures, slides, and course materials",

    webinarName: "Webinar",
    webinarTitle: "a Webinar",
    webinarPhrase: "a webinar",
    webinarNoun: "webinar",
    webinarDescription: "live and recorded online webinars and web seminars",

    onlineCourseName: "Online Course",
    onlineCourseTitle: "an Online Course",
    onlineCoursePhrase: "an online course",
    onlineCourseNoun: "online course",
    onlineCourseDescription:
      "online courses and MOOCs (Coursera, edX, Udemy, FutureLearn)",

    presentationSlidesName: "PowerPoint Presentation",
    presentationSlidesTitle: "a PowerPoint Presentation",
    presentationSlidesPhrase: "a PowerPoint presentation",
    presentationSlidesNoun: "PowerPoint presentation",
    presentationSlidesDescription: "PowerPoint and presentation slide decks",

    imageName: "Image or Photograph",
    imageTitle: "a Image or Photograph",
    imagePhrase: "a image or photograph",
    imageNoun: "image or photograph",
    imageDescription: "photographs, artwork, charts, and online images",

    movieName: "Movie or Film",
    movieTitle: "a Movie or Film",
    moviePhrase: "a movie or film",
    movieNoun: "movie or film",
    movieDescription: "a movie or film",

    documentaryName: "Documentary",
    documentaryTitle: "a Documentary",
    documentaryPhrase: "a documentary",
    documentaryNoun: "documentary",
    documentaryDescription: "documentary films and non-fiction films",

    tvShowName: "TV Show",
    tvShowTitle: "a TV Show",
    tvShowPhrase: "a TV show",
    tvShowNoun: "TV show",
    tvShowDescription: "a TV show or series",

    songName: "Song or Music",
    songTitle: "a Song or Music",
    songPhrase: "a song or music",
    songNoun: "song or music",
    songDescription: "a song or music recording",

    poemName: "Poem",
    poemTitle: "a Poem",
    poemPhrase: "a poem",
    poemNoun: "poem",
    poemDescription: "poems, verse, and poetry from anthologies or collections",

    courtCaseName: "Court Case",
    courtCaseTitle: "a Court Case",
    courtCasePhrase: "a court case",
    courtCaseNoun: "court case",
    courtCaseDescription: "a court case or legal decision",

    patentName: "Patent",
    patentTitle: "a Patent",
    patentPhrase: "a patent",
    patentNoun: "patent",
    patentDescription: "a patent or intellectual property filing",

    encyclopediaName: "Encyclopedia Entry",
    encyclopediaTitle: "a Encyclopedia Entry",
    encyclopediaPhrase: "a encyclopedia entry",
    encyclopediaNoun: "encyclopedia entry",
    encyclopediaDescription: "an encyclopedia article or entry",

    dictionaryName: "Dictionary Entry",
    dictionaryTitle: "a Dictionary Entry",
    dictionaryPhrase: "a dictionary entry",
    dictionaryNoun: "dictionary entry",
    dictionaryDescription: "a dictionary definition or entry",

    datasetName: "Dataset",
    datasetTitle: "a Dataset",
    datasetPhrase: "a dataset",
    datasetNoun: "dataset",
    datasetDescription: "a dataset or database",

    softwareName: "Software or App",
    softwareTitle: "a Software or App",
    softwarePhrase: "a software or app",
    softwareNoun: "software or app",
    softwareDescription: "software, an app, or computer program",

    videoGameName: "Video Game",
    videoGameTitle: "a Video Game",
    videoGamePhrase: "a video game",
    videoGameNoun: "video game",
    videoGameDescription: "video games and interactive game software",

    pressReleaseName: "Press Release",
    pressReleaseTitle: "a Press Release",
    pressReleasePhrase: "a press release",
    pressReleaseNoun: "press release",
    pressReleaseDescription: "a press release or news release",

    reportName: "Report",
    reportTitle: "a Report",
    reportPhrase: "a report",
    reportNoun: "report",
    reportDescription: "a report or white paper",

    preprintName: "Preprint",
    preprintTitle: "a Preprint",
    preprintPhrase: "a preprint",
    preprintNoun: "preprint",
    preprintDescription: "arXiv, bioRxiv, and other preprint server papers",

    bookChapterName: "Book Chapter",
    bookChapterTitle: "a Book Chapter",
    bookChapterPhrase: "a book chapter",
    bookChapterNoun: "book chapter",
    bookChapterDescription: "chapters and essays in edited books",

    chatgptName: "ChatGPT",
    chatgptTitle: "ChatGPT",
    chatgptPhrase: "ChatGPT",
    chatgptNoun: "ChatGPT",
    chatgptDescription: "ChatGPT and other generative AI tools",

    bibleName: "Bible",
    bibleTitle: "the Bible",
    biblePhrase: "the Bible",
    bibleNoun: "Bible",
    bibleDescription: "the Bible, scripture, and other sacred texts",

    tedTalkName: "TED Talk",
    tedTalkTitle: "a TED Talk",
    tedTalkPhrase: "a TED Talk",
    tedTalkNoun: "TED Talk",
    tedTalkDescription: "TED Talks and recorded conference talks",

    emailName: "Email",
    emailTitle: "an Email",
    emailPhrase: "an email",
    emailNoun: "email",
    emailDescription: "emails, letters, and personal communications",

    redditName: "Reddit or Forum Post",
    redditTitle: "a Reddit or Forum Post",
    redditPhrase: "a Reddit post",
    redditNoun: "Reddit post",
    redditDescription:
      "Reddit threads, AMAs, and online forum or discussion-board posts",

    tweetName: "X (Twitter) Post",
    tweetTitle: "an X (Twitter) Post",
    tweetPhrase: "an X post",
    tweetNoun: "X post",
    tweetDescription: "tweets and posts on X (formerly Twitter)",

    mapName: "Map",
    mapTitle: "a Map",
    mapPhrase: "a map",
    mapNoun: "map",
    mapDescription: "printed maps, atlases, and dynamic maps like Google Maps",

    claudeName: "Claude",
    claudeTitle: "Claude",
    claudePhrase: "Claude",
    claudeNoun: "Claude",
    claudeDescription: "Claude chats and other Anthropic AI output",

    geminiName: "Gemini",
    geminiTitle: "Gemini",
    geminiPhrase: "Gemini",
    geminiNoun: "Gemini",
    geminiDescription: "Google Gemini chats and AI output",

    copilotName: "Microsoft Copilot",
    copilotTitle: "Microsoft Copilot",
    copilotPhrase: "Microsoft Copilot",
    copilotNoun: "Microsoft Copilot",
    copilotDescription: "Microsoft Copilot chats and AI answers",

    perplexityName: "Perplexity",
    perplexityTitle: "Perplexity",
    perplexityPhrase: "Perplexity",
    perplexityNoun: "Perplexity",
    perplexityDescription:
      "Perplexity AI answers, threads, and the sources they cite",

    aiImageName: "AI-Generated Image",
    aiImageTitle: "an AI-Generated Image",
    aiImagePhrase: "an AI-generated image",
    aiImageNoun: "AI-generated image",
    aiImageDescription:
      "images made with DALL·E, Midjourney, Gemini, and other AI tools",

    tiktokName: "TikTok Video",
    tiktokTitle: "a TikTok Video",
    tiktokPhrase: "a TikTok video",
    tiktokNoun: "TikTok video",
    tiktokDescription: "TikTok videos and creator posts",

    instagramName: "Instagram Post",
    instagramTitle: "an Instagram Post",
    instagramPhrase: "an Instagram post",
    instagramNoun: "Instagram post",
    instagramDescription: "Instagram posts, reels, and stories",

    statuteName: "Law or Statute",
    statuteTitle: "a Law or Statute",
    statutePhrase: "a law or statute",
    statuteNoun: "law or statute",
    statuteDescription: "acts of a legislature, statutes, and codified laws",

    isoStandardName: "ISO Standard",
    isoStandardTitle: "an ISO Standard",
    isoStandardPhrase: "an ISO standard",
    isoStandardNoun: "ISO standard",
    isoStandardDescription: "ISO, IEC, and other published technical standards",

    quranName: "Quran",
    quranTitle: "the Quran",
    quranPhrase: "the Quran",
    quranNoun: "Quran",
    quranDescription: "the Quran, in Arabic or in translation",

    ebookNoPageNumbersName: "E-book Without Page Numbers",
    ebookNoPageNumbersTitle: "an E-book Without Page Numbers",
    ebookNoPageNumbersPhrase: "an e-book without page numbers",
    ebookNoPageNumbersNoun: "e-book without page numbers",
    ebookNoPageNumbersDescription:
      "Kindle, EPUB, and online e-books that have no fixed page numbers",

    speechName: "Speech",
    speechTitle: "a Speech",
    speechPhrase: "a speech",
    speechNoun: "speech",
    speechDescription: "speeches, addresses, and sermons, live or recorded",

    letterName: "Letter",
    letterTitle: "a Letter",
    letterPhrase: "a letter",
    letterNoun: "letter",
    letterDescription:
      "published letters, letters in archives, and open letters",

    secondarySourceName: "Secondary Source",
    secondarySourceTitle: "a Secondary Source",
    secondarySourcePhrase: "a secondary source",
    secondarySourceNoun: "secondary source",
    secondarySourceDescription:
      "a source you found quoted or cited in another work",
  },

  // The three browse-by-facet pages under `/<locale>/citation-styles/…` — 32 academic
  // fields, 68 countries and 39 subject categories, 139 pages in one section because
  // they share a shell, a breadcrumb trail and a styles browser.
  //
  // The style NAMES, slugs and the CSL catalog itself stay in `src/data/**`: those are
  // subject matter. What the facets themselves are called is not — "Political Science",
  // "United Kingdom" and "Film & Media" are ordinary nouns that every reader expects in
  // their own language, and there are only 139 of them in total (see the `academicFields`,
  // `regions` and `styleCategories` sections below).
  taxonomyPage: {
    // ---- /citation-styles/field/<slug> ----
    fieldNotFoundTitle: "Not Found",
    fieldMetaTitle:
      "Citation Styles for {field} - Best Referencing Styles [2026]",
    fieldMetaDescription:
      "Discover the most commonly used citation styles in {field}. Find the right referencing format for your {fieldLower} paper, thesis, or journal submission.",
    fieldOgTitle: "Citation styles for {field}",
    fieldOgSubtitle: "Commonly required: {styles}.",
    fieldOgKind: "By academic field",
    fieldKeywordStyle: "{field} citation style",
    fieldKeywordReferencing: "{field} referencing",
    fieldKeywordBest: "best citation style for {fieldLower}",
    fieldKeywordBibliography: "{fieldLower} bibliography format",
    fieldKeywordDiscipline: "citation styles by discipline",
    fieldBackLink: "← All Citation Styles",
    fieldHeading: "Citation Styles for {field}",
    fieldRecommendedHeading: "Recommended Citation Styles",
    fieldRecommendedIntro:
      "The following citation styles are commonly used in {field} research, journals, and coursework. Select a style to learn more and start generating citations.",
    fieldStyleFallback:
      "A citation style used in {field} and related disciplines.",
    fieldRelatedHeading: "Related Fields",
    fieldRelatedIntro:
      "Explore citation styles used in related academic disciplines:",
    fieldCtaHeading: "Explore All Citation Styles",
    fieldCtaBody:
      "CitationEasy supports {count} citation styles across every academic discipline. Browse the full collection to find the exact format you need.",
    fieldCtaButton: "Browse All Styles →",
    fieldFaqWhichQ: "What citation style is used in {field}?",
    fieldFaqWhichA:
      "The citation style used in {field} depends on the specific journal, institution, or instructor requirements. Common styles include {styles}. Always check your assignment guidelines or target journal for the required format.",
    fieldFaqSameQ: "Do all {field} journals use the same style?",
    fieldFaqSameA:
      "No, {field} journals do not all use the same citation style. While certain styles are more common in the field, individual journals may have their own formatting requirements. Always check the journal's submission guidelines before preparing your manuscript.",
    fieldFaqChooseQ: "How do I choose a citation style for my {field} paper?",
    fieldFaqChooseA:
      "To choose a citation style for your {field} paper, first check if your instructor or target journal specifies a required style. If not, use one of the commonly recommended styles for {field} listed above. CitationEasy supports over {count} styles and can generate citations in any of them for free.",

    // ---- /citation-styles/country/<slug> ----
    countryNotFoundTitle: "Country not found",
    countryMetaTitle: "{country} Citation Styles - APA, MLA & More",
    countryMetaDescription:
      "Browse citation styles used in {country}. Free citation generator for {country} formats.",
    countryOgTitle: "Citation styles used in {country}",
    countryOgKind: "By country",
    countryHeading: "{country} citation styles",
    countryIntroOne:
      "{count} citation style commonly used in or associated with {country}. Choose one to open a dedicated generator.",
    countryIntroMany:
      "{count} citation styles commonly used in or associated with {country}. Choose one to open a dedicated generator.",
    countryBrowserTitle: "{country} Citation Styles",

    // ---- /citation-styles/type/<slug> ----
    typeNotFoundTitle: "Type not found",
    typeMetaTitle: "{type} Citation Styles - Free Generator",
    typeMetaDescription:
      "Citation styles for {typeLower}. APA, MLA, Vancouver, and more. Free citation generator.",
    typeOgTitle: "{type} citation styles",
    typeOgKind: "By subject",
    typeHeading: "{type} citation styles",
    typeIntroOne:
      "{count} citation style for {typeLower}. Choose one to open a dedicated generator with FAQs.",
    typeIntroMany:
      "{count} citation styles for {typeLower}. Choose one to open a dedicated generator with FAQs.",
    typeBrowserTitle: "{type} Citation Styles",

    // ---- Shared chrome ----
    breadcrumbStyles: "Citation styles",
    // Title case, because the BreadcrumbList markup says "Citation Styles" while the
    // visible trail says "Citation styles". Two keys rather than one so translating
    // either does not silently change the other.
    breadcrumbStylesSchema: "Citation Styles",
    breadcrumbByCountry: "By country",
    breadcrumbByDiscipline: "By discipline",
    allStylesLink: "← All citation styles",
    internationalLabel: "International",
  },

  // The 32 academic fields (`ACADEMIC_FIELDS` in src/data/style-pages.ts), keyed by
  // slug. Their one-sentence `description` is next door in `academicFieldDescriptions`.
  academicFields: {
    psychology: "Psychology",
    education: "Education",
    sociology: "Sociology",
    anthropology: "Anthropology",
    politicalScience: "Political Science",
    economics: "Economics",
    business: "Business & Management",
    literature: "Literature & Languages",
    history: "History",
    philosophy: "Philosophy",
    law: "Law & Legal Studies",
    medicine: "Medicine & Clinical Sciences",
    nursing: "Nursing & Midwifery",
    publicHealth: "Public Health & Epidemiology",
    pharmacy: "Pharmacy",
    biology: "Biology & Life Sciences",
    chemistry: "Chemistry",
    physics: "Physics",
    engineering: "Engineering",
    computerScience: "Computer Science",
    mathematics: "Mathematics & Statistics",
    environmentalScience: "Environmental Science",
    earthScience: "Earth & Geological Sciences",
    artHistory: "Art History & Visual Arts",
    music: "Music & Musicology",
    theology: "Theology & Religious Studies",
    socialWork: "Social Work",
    linguistics: "Linguistics",
    architecture: "Architecture & Urban Planning",
    filmMedia: "Film & Media Studies",
    journalism: "Journalism & Communication",
    criminology: "Criminology & Criminal Justice",
  },

  // The same 32 fields' one-sentence ledes — the opening paragraph of every
  // `/citation-styles/field/<slug>` page.
  //
  // Unlike everything above, these are hand-written per field rather than mail-merged
  // from a template, which is why the extraction pass left them in
  // `src/data/style-pages.ts`: translating them is a content job, not a string swap.
  // But "hand-written" describes how they were produced, not where they belong. With
  // the chrome translated and the lede still English, the page is the hybrid CLAUDE.md
  // calls worse than an honest redirect — translated furniture around an English core.
  // `academicFieldDescription()` (src/lib/i18n/data-labels.ts) falls back to the data
  // file's own value, so a field added upstream is untranslated rather than blank.
  academicFieldDescriptions: {
    psychology:
      "Citation styles commonly used in psychology, behavioral science, and cognitive research.",
    education:
      "Citation styles for education research, pedagogy, and curriculum studies.",
    sociology:
      "Citation styles used in sociological research and social theory.",
    anthropology:
      "Citation styles for anthropological and ethnographic research.",
    politicalScience:
      "Citation styles for political science, government, and public policy research.",
    economics: "Citation styles used in economics and econometrics research.",
    business:
      "Citation styles for business, management, marketing, and finance research.",
    literature:
      "Citation styles for literary studies, linguistics, and language research.",
    history:
      "Citation styles for historical research, historiography, and archival studies.",
    philosophy:
      "Citation styles for philosophical writing and ethics research.",
    law: "Legal citation styles for court documents, law reviews, and legal scholarship.",
    medicine:
      "Citation styles for medical research, clinical studies, and healthcare.",
    nursing: "Citation styles for nursing research and healthcare practice.",
    publicHealth:
      "Citation styles for public health research and epidemiological studies.",
    pharmacy:
      "Citation styles for pharmaceutical and pharmacological research.",
    biology: "Citation styles for biological research, ecology, and genetics.",
    chemistry:
      "Citation styles for chemistry, biochemistry, and materials science research.",
    physics:
      "Citation styles for physics, astrophysics, and applied physics research.",
    engineering:
      "Citation styles for engineering and applied technology research.",
    computerScience:
      "Citation styles for computer science, AI, and software engineering research.",
    mathematics:
      "Citation styles for mathematics, applied math, and statistics research.",
    environmentalScience:
      "Citation styles for environmental research and sustainability studies.",
    earthScience:
      "Citation styles for geology, geophysics, and earth science research.",
    artHistory:
      "Citation styles for art history, visual culture, and museum studies.",
    music:
      "Citation styles for music research, theory, and performance studies.",
    theology: "Citation styles for theological and religious scholarship.",
    socialWork: "Citation styles for social work research and practice.",
    linguistics:
      "Citation styles for linguistics, phonetics, and language science.",
    architecture:
      "Citation styles for architecture, urban design, and planning research.",
    filmMedia:
      "Citation styles for film, television, media, and communication studies.",
    journalism:
      "Citation styles for journalism, media studies, and communication research.",
    criminology:
      "Citation styles for criminology and criminal justice research.",
  },

  // The 68 country/region facets (`REGIONS` in src/data/citation-styles.ts), keyed by
  // slug. `getRegionCountryCode` still keys off the English data value, so translating these
  // moves the label without moving the flag.
  regions: {
    unitedStates: "United States",
    unitedKingdom: "United Kingdom",
    australia: "Australia",
    canada: "Canada",
    newZealand: "New Zealand",
    ireland: "Ireland",
    southAfrica: "South Africa",
    germany: "Germany",
    france: "France",
    spain: "Spain",
    italy: "Italy",
    netherlands: "Netherlands",
    belgium: "Belgium",
    switzerland: "Switzerland",
    austria: "Austria",
    portugal: "Portugal",
    luxembourg: "Luxembourg",
    sweden: "Sweden",
    norway: "Norway",
    denmark: "Denmark",
    finland: "Finland",
    iceland: "Iceland",
    poland: "Poland",
    czechRepublic: "Czech Republic",
    hungary: "Hungary",
    romania: "Romania",
    russia: "Russia",
    ukraine: "Ukraine",
    greece: "Greece",
    turkey: "Turkey",
    croatia: "Croatia",
    slovenia: "Slovenia",
    slovakia: "Slovakia",
    serbia: "Serbia",
    bulgaria: "Bulgaria",
    lithuania: "Lithuania",
    latvia: "Latvia",
    estonia: "Estonia",
    japan: "Japan",
    china: "China",
    southKorea: "South Korea",
    india: "India",
    singapore: "Singapore",
    hongKong: "Hong Kong",
    taiwan: "Taiwan",
    malaysia: "Malaysia",
    indonesia: "Indonesia",
    thailand: "Thailand",
    vietnam: "Vietnam",
    philippines: "Philippines",
    pakistan: "Pakistan",
    bangladesh: "Bangladesh",
    sriLanka: "Sri Lanka",
    israel: "Israel",
    saudiArabia: "Saudi Arabia",
    unitedArabEmirates: "United Arab Emirates",
    brazil: "Brazil",
    mexico: "Mexico",
    argentina: "Argentina",
    chile: "Chile",
    colombia: "Colombia",
    peru: "Peru",
    venezuela: "Venezuela",
    egypt: "Egypt",
    nigeria: "Nigeria",
    kenya: "Kenya",
    morocco: "Morocco",
    international: "International",
  },

  // The 39 subject categories (`CATEGORIES` in src/data/citation-styles.ts), keyed by
  // slug. The emoji beside each one stays in the data — it is language-neutral.
  styleCategories: {
    medicine: "Medicine",
    nursing: "Nursing",
    publicHealth: "Public Health",
    pharmacy: "Pharmacy",
    dentistry: "Dentistry",
    veterinary: "Veterinary Science",
    psychology: "Psychology",
    sociology: "Sociology",
    anthropology: "Anthropology",
    politicalScience: "Political Science",
    economics: "Economics",
    education: "Education",
    socialWork: "Social Work",
    biology: "Biology",
    chemistry: "Chemistry",
    physics: "Physics",
    environmentalScience: "Environmental Science",
    earthScience: "Earth Science & Geology",
    agriculture: "Agriculture",
    ecology: "Ecology",
    engineering: "Engineering",
    computerScience: "Computer Science",
    mathematics: "Mathematics",
    statistics: "Statistics",
    history: "History",
    literature: "Literature",
    philosophy: "Philosophy",
    linguistics: "Linguistics",
    artHistory: "Art History",
    music: "Music",
    filmMedia: "Film & Media",
    archaeology: "Archaeology",
    law: "Law",
    business: "Business",
    architecture: "Architecture",
    religion: "Religion & Theology",
    geography: "Geography",
    general: "General / Multidisciplinary",
    other: "Other",
  },

  // The closed vocabularies `STYLE_LANDING_PAGES` (src/data/style-pages.ts) draws on for
  // the 30 major styles: what each style calls its end-of-paper list, the shape of its
  // in-text citation, its edition, and the disciplines that require it. The comparison
  // pages print all four in a table and the field pages print two of them under every
  // style card, so leaving them in the data means a translated page with an English
  // table in the middle of it.
  //
  // Style NAMES (`name`, `fullName`) are NOT here: a style's official name is what it
  // is called everywhere. The 30 hand-written taglines are, in `styleTaglines` below.
  styleFacts: {
    // `referenceListName` — 6 distinct values across the 30 styles.
    listReferenceList: "Reference List",
    listWorksCited: "Works Cited",
    listReferences: "References",
    listBibliography: "Bibliography",
    listTableOfAuthorities: "Table of Authorities",
    listReferencesCited: "References Cited",
    listLowerReferenceList: "reference list",
    listLowerWorksCited: "works cited",
    listLowerReferences: "references",
    listLowerBibliography: "bibliography",
    listLowerTableOfAuthorities: "table of authorities",
    listLowerReferencesCited: "references cited",

    // `inTextFormat` — 14 distinct values. "Author", "Year" and "Page" are slot names,
    // not part of the citation, so they read as English words on a Spanish page.
    inTextAuthorCommaYear: "(Author, Year)",
    inTextAuthorPage: "(Author Page)",
    inTextAuthorYear: "(Author Year)",
    inTextSuperscriptOrBracketed: "Superscript or bracketed numbers (1)",
    inTextBracketedNumbers: "Bracketed numbers [1]",
    inTextSuperscriptNumbers: "Superscript numbers¹",
    inTextSuperscriptOrAuthorYear: "Superscript numbers or (Author Year)",
    inTextKeyBased: "Key-based references",
    inTextSuperscriptOrAuthorCommaYear: "Superscript numbers or (Author, Year)",
    inTextFootnotesCaseLegislation:
      "Footnotes with case and legislation citations",
    inTextFootnotesSignals: "Footnotes with signal abbreviations",
    inTextFootnotesPinpoint: "Footnotes with pinpoint references",
    inTextParenNumbers: "Bracketed numbers (1)",
    inTextItalicNumbers: "Italic numbers (1)",

    // `edition` — 12 distinct values. The ordinal and the word "edition" translate; the
    // handbook name inside "Cite Them Right 11th edition" does not.
    edition4th: "4th edition",
    edition5th: "5th edition",
    edition7th: "7th edition",
    edition8th: "8th edition",
    edition9th: "9th edition",
    edition11th: "11th edition",
    edition18th: "18th edition",
    edition22nd: "22nd edition",
    editionCiteThemRight11th: "Cite Them Right 11th edition",
    editionIcmjeStandard: "ICMJE standard",
    editionStandard: "standard",
    editionCurrent: "current",

    // `fields` — the 67 discipline names the 30 styles are tagged with.
    fieldAcoustics: "Acoustics",
    fieldAnthropology: "Anthropology",
    fieldAppliedPhysics: "Applied Physics",
    fieldArchaeology: "Archaeology",
    fieldArts: "Arts",
    fieldAstrophysics: "Astrophysics",
    fieldAustralianLaw: "Australian Law",
    fieldBiochemistry: "Biochemistry",
    fieldBiology: "Biology",
    fieldBiomedicalSciences: "Biomedical Sciences",
    fieldBiotechnology: "Biotechnology",
    fieldBusiness: "Business",
    fieldCellBiology: "Cell Biology",
    fieldChemicalEngineering: "Chemical Engineering",
    fieldChemistry: "Chemistry",
    fieldClinicalResearch: "Clinical Research",
    fieldComputerScience: "Computer Science",
    fieldCondensedMatter: "Condensed Matter",
    fieldCourtDocuments: "Court Documents",
    fieldCriminology: "Criminology",
    fieldCulturalStudies: "Cultural Studies",
    fieldDemography: "Demography",
    fieldEcology: "Ecology",
    fieldEducation: "Education",
    fieldElectronics: "Electronics",
    fieldEngineering: "Engineering",
    fieldEnvironmentalScience: "Environmental Science",
    fieldEpidemiology: "Epidemiology",
    fieldEthnography: "Ethnography",
    fieldGenetics: "Genetics",
    fieldGeology: "Geology",
    fieldGlobalHealth: "Global Health",
    fieldHealthSciences: "Health Sciences",
    fieldHistory: "History",
    fieldHumanities: "Humanities",
    fieldInformationSystems: "Information Systems",
    fieldInformationTechnology: "Information Technology",
    fieldInternationalRelations: "International Relations",
    fieldJurisprudence: "Jurisprudence",
    fieldLanguages: "Languages",
    fieldLaw: "Law",
    fieldLawReview: "Law Review",
    fieldLegalStudies: "Legal Studies",
    fieldLifeSciences: "Life Sciences",
    fieldLiterature: "Literature",
    fieldMaterialsScience: "Materials Science",
    fieldMathematics: "Mathematics",
    fieldMedicine: "Medicine",
    fieldMolecularBiology: "Molecular Biology",
    fieldMultidisciplinary: "Multidisciplinary",
    fieldMultidisciplinaryScience: "Multidisciplinary Science",
    fieldNaturalSciences: "Natural Sciences",
    fieldNursing: "Nursing",
    fieldPharmacy: "Pharmacy",
    fieldPhysics: "Physics",
    fieldPoliticalScience: "Political Science",
    fieldPsychology: "Psychology",
    fieldPublicHealth: "Public Health",
    fieldPublicPolicy: "Public Policy",
    fieldPublishing: "Publishing",
    fieldScience: "Science",
    fieldSciences: "Sciences",
    fieldSocialSciences: "Social Sciences",
    fieldSocialWork: "Social Work",
    fieldSociology: "Sociology",
    fieldSoftwareEngineering: "Software Engineering",
    fieldTheology: "Theology",
  },

  // The 30 major styles' hero taglines (`STYLE_LANDING_PAGES[].tagline`), keyed by
  // slug.
  //
  // Per-page hand-written copy, not a template — which is why they outlived the
  // extraction pass in `src/data/style-pages.ts`. Three page types print them: the
  // style landing page's hero, the card for each recommended style on a field
  // taxonomy page, and the lower-cased clause inside a comparison page's opening
  // sentence ("APA (…) is the most widely used citation style in the social
  // sciences"). Leaving them in the data means an English sentence in the first
  // paragraph of an otherwise translated page. `styleTagline()`
  // (src/lib/i18n/data-labels.ts) falls back to the data file's own value.
  styleTaglines: {
    apa: "The most widely used citation style in the social sciences",
    mla: "The standard citation style for the humanities",
    chicago:
      "America's most trusted style guide for authors, editors, and publishers",
    harvard:
      "The most popular referencing style in UK and Australian universities",
    vancouver:
      "The standard citation style for medical and biomedical journals",
    ieee: "The go-to citation style for engineering and computer science",
    ama: "The citation standard for American medical journals",
    turabian: "The student-friendly version of Chicago style",
    cse: "The preferred citation style for the natural and physical sciences",
    bibtex: "The bibliography format for LaTeX users and academic typesetting",
    nature:
      "The citation style used by the world's leading multidisciplinary journal",
    acs: "The citation standard for chemistry and chemical sciences",
    oscola: "The standard legal citation style for UK law schools and journals",
    bluebook:
      "The definitive legal citation system for US courts and law reviews",
    aglc: "The standard legal citation style for Australian law schools and courts",
    asa: "The standard citation style for sociology and social research",
    apsa: "The citation style for political science research and journals",
    aaa: "The standard citation style for anthropological research",
    nlm: "The citation standard used by PubMed and the National Library of Medicine",
    acm: "The citation style for ACM conferences and computing research",
    aps: "The citation style for Physical Review and APS journals",
    aip: "The citation style for AIP and physics journals",
    cell: "The citation style for Cell Press and leading biology journals",
    science: "The citation style for Science magazine and AAAS journals",
    plos: "The citation style for PLOS open-access journals",
    bmj: "The citation style for BMJ and leading British medical journals",
    lancet: "The citation style for The Lancet family of medical journals",
    jama: "The citation style for JAMA and the JAMA Network journals",
    elsevierHarvard: "The Harvard-style variant used across Elsevier journals",
    springer: "The citation style for Springer Nature journals and books",
  },

  // The 105 style-versus-style pages at `/<locale>/compare/<slug>`.
  //
  // The two style names arrive as placeholder values and are never translated. The
  // facts in the table come from `styleFacts` above and the taglines from
  // `styleTaglines`; only `fullName` stays in `src/data/style-pages.ts`, because an
  // official name is the same in every language.
  comparePage: {
    notFoundTitle: "Not Found",
    metaTitle: "{a} vs {b} - Key Differences Explained [2026]",
    metaDescription:
      "Compare {a} and {b} citation styles side by side: the key differences in formatting, in-text citations, and reference lists, so you can pick the right one.",
    ogTitle: "{a} vs {b}",
    ogSubtitle:
      "In-text format, reference entries, capitalisation, and which disciplines require each.",
    ogKind: "Style comparison",
    keywordVs: "{a} vs {b}",
    keywordVersus: "{a} versus {b}",
    keywordDifference: "difference between {a} and {b}",
    keywordOr: "{a} or {b}",
    keywordComparison: "citation style comparison",
    breadcrumbCompare: "Compare Styles",
    breadcrumbCurrent: "{a} vs {b}",
    backLink: "← Back to Comparison Tool",
    heading: "{a} vs {b}: Key Differences",
    intro:
      "{a} and {b} are two of the most widely used citation styles in academia, but they differ in important ways. {introA} {introB} Understanding the differences will help you choose the right format for your paper.",
    styleIntro: "{style} ({fullName}) {tagline}.",
    styleIntroFallback:
      "{style} is a popular citation style used across many academic disciplines.",
    tableHeading: "Side-by-Side Comparison",
    tableFeature: "Feature",
    tableCitationSystem: "Citation System",
    tableInText: "In-Text Format",
    tableReferenceList: "Reference List Name",
    tableEdition: "Edition",
    tableCommonFields: "Common Fields",
    // Title case, for the table cell. `styleLanding.system*` says "Author–date" for the
    // fact list on the style landing pages; both spellings are deliberate.
    systemAuthorDate: "Author-Date",
    systemNumbered: "Numbered",
    systemFootnotes: "Footnotes",
    // The same three systems inside a sentence ("uses a numbered citation system").
    describeAuthorDate: "parenthetical author-date",
    describeNumbered: "numbered",
    describeFootnotes: "footnote-based",
    whenToUseHeading: "When to Use {style}",
    whenToUseBody:
      "{style} ({fullName}) uses a {system} citation system with {inText} in-text citations and a {list} at the end of the paper. It is most commonly used in {fields}{more}.",
    whenToUseMoreFields: ", and related fields",
    whenToUseFallback:
      "{style} is a widely recognized citation style. Check your assignment guidelines or target journal to confirm whether {style} is the required format.",
    ctaHeading: "See the Difference in Action",
    ctaBodyBefore: "Try our",
    ctaBodyLink: "interactive comparison tool",
    ctaBodyAfter:
      "to compare {a} and {b} citations side by side using your own sources.",
    ctaCiteIn: "Cite in {style} →",
    faqDifferenceQ: "What is the difference between {a} and {b}?",
    faqDifferenceA:
      "{a} and {b} differ in their in-text citation format, reference list structure, and the academic disciplines that use them. {detail}",
    faqDifferenceDetail:
      "{a} uses {inTextA} in-text citations with a {listA}, while {b} uses {inTextB} with a {listB}.",
    faqDifferenceDetailFallback:
      "Each style has distinct rules for formatting citations, organizing bibliographies, and handling different source types.",
    faqBetterQ: "Which is better, {a} or {b}?",
    faqBetterA:
      "Neither {a} nor {b} is inherently better. The right choice depends on your academic discipline, your instructor's requirements, or the journal you are submitting to. Check your assignment guidelines or target publication to determine which style to use.",
    faqSwitchQ: "Can I switch between {a} and {b}?",
    faqSwitchA:
      "Yes, you can switch between {a} and {b} using CitationEasy's free citation generator. Simply select the desired style and your citations will be reformatted automatically. However, you should only use one style per paper unless your instructor says otherwise.",
  },

  // The ~21 converter-pair pages at `/<locale>/convert/<slug>`.
  //
  // Only the shell is here; each pair's own four strings are in `converterCopy` below.
  // The format names (DOI, ISBN, BibTeX, RIS, CSL-JSON, PubMed ID, arXiv ID, Word) are
  // identifiers, never translated.
  converterPage: {
    notFoundTitle: "Converter not found",
    metaTitle: "{title} - Free & Instant",
    ogTitle: "{title}",
    ogKind: "Converter",
    breadcrumbConverters: "Converters",
    breadcrumbLabel: "Breadcrumb",
    whenHeading: "When you need this",
    relatedHeading: "Related converters",
    allConverters: "All converters",
    formatConverterTool: "Citation Format Converter",
    faqHowQ: "How do I convert {input} to {output}?",
    faqHowA:
      "{intro} Everything is free, with no account and no conversion limit.",
    faqPrivacyQ: "Is my data uploaded anywhere?",
    faqPrivacyIdentifier:
      "Each {input} is looked up against the relevant public metadata service (Crossref for DOIs, PubMed for PMIDs, library records for ISBNs), and only the identifier itself is sent — never your documents. The formatted results are yours to copy or download; nothing is stored.",
    faqPrivacyLocal:
      "No — the {input} you paste or upload is parsed entirely in your browser. {detail}",
    faqPrivacyDetailStyle:
      "Only the parsed reference fields (author, title, year) are sent to our formatter to render the citations; your file itself never leaves your device.",
    faqPrivacyDetailOffline:
      "The conversion runs fully offline in the page — nothing is sent to any server.",
    faqUsefulQ: "When is a {input} to {output} conversion useful?",
    faqOtherQ: "Can I convert to other styles and formats too?",
    faqOtherA:
      "Yes. CitationEasy's generator formats any source in {count} citation styles, the bibliography exports Word, RTF, BibTeX, RIS, and CSL-JSON, and the converter hub lists every direct conversion pair. If you need a style-to-style rewrite of already-formatted text, use the Citation Format Converter tool instead.",
  },

  // The 21 converter pairs' own copy (`CONVERTER_PAIRS` in src/data/converters.ts),
  // keyed by slug: title, meta description, the intro paragraph under the H1, and the
  // "when you need this" paragraph.
  //
  // Four hand-written strings per pair, and `src/data/converters.ts` has a quality rule
  // saying they must stay hand-written — no mail-merged sentences, because mass-templated
  // pages are what earned the site a scaled-content penalty. That rule is about how the
  // copy is authored; it says nothing about which language a reader gets it in. Left in
  // the data file they are the entire body of an otherwise translated page.
  // `converterPairCopy()` (src/lib/i18n/data-labels.ts) returns all four at once and
  // falls back to the data file per field, so a converter added upstream ships English
  // copy rather than a blank page.
  converterCopy: {
    doiToApaTitle: "DOI to APA Citation Converter",
    doiToApaMetaDescription:
      "Paste DOIs (one per line) and get APA 7 references instantly, pulled from the publisher's official Crossref metadata. Free, no account.",
    doiToApaIntro:
      "A DOI already points at the publisher's authoritative metadata, so there is no reason to type an APA reference by hand. Paste one DOI per line and each resolves to a complete APA 7 reference — authors inverted, sentence-case title, italicized journal, and the https://doi.org/ link APA requires.",
    doiToApaUseCase:
      "Typical moment: your professor wants an APA reference list, and all you have is the pile of DOIs you saved while reading. Paste the pile, copy the list.",
    doiToMlaTitle: "DOI to MLA Citation Converter",
    doiToMlaMetaDescription:
      "Turn DOIs into MLA 9 works-cited entries automatically using Crossref metadata. Paste one DOI per line — free, nothing to install.",
    doiToMlaIntro:
      "MLA 9 treats a journal article as a container chain — article title in quotes, journal in italics, volume, issue, year, pages — which is fiddly to assemble manually. A DOI lookup fills every slot from the publisher's record, so the works-cited entry comes out right the first time.",
    doiToMlaUseCase:
      "Handy when a humanities course requires MLA but your sources come from science-leaning databases that only hand you DOIs.",
    doiToBibtexTitle: "DOI to BibTeX Converter",
    doiToBibtexMetaDescription:
      "Convert DOIs to BibTeX entries for LaTeX and Overleaf. Paste one DOI per line, download a ready .bib — free and unlimited.",
    doiToBibtexIntro:
      "Every LaTeX paper eventually needs its .bib file. Paste DOIs one per line and get clean @article entries with generated cite keys, ready for Overleaf, natbib, or biblatex — no manual field copying from publisher pages.",
    doiToBibtexUseCase:
      "The classic flow: a reviewer sends you three papers to cite, you have their DOIs, and your deadline is tonight. Paste, download, \\cite away.",
    doiToRisTitle: "DOI to RIS Converter",
    doiToRisMetaDescription:
      "Convert DOIs into a RIS file that Zotero, Mendeley, and EndNote import directly. Paste DOIs one per line — free, no sign-up.",
    doiToRisIntro:
      "RIS is the interchange format every reference manager imports. Paste DOIs and download a .ris file whose TY/AU/TI/T2 fields come straight from Crossref — then File → Import it into Zotero, Mendeley, or EndNote and the entries arrive fully structured.",
    doiToRisUseCase:
      "Perfect for migrating a hand-collected DOI list into a proper reference manager without retyping a single author name.",
    isbnToApaTitle: "ISBN to APA Citation Converter",
    isbnToApaMetaDescription:
      "Turn ISBNs into APA 7 book references automatically. Paste one ISBN per line and copy the formatted list — free, no account.",
    isbnToApaIntro:
      "The ISBN on a book's copyright page is all you need for its APA reference. Each ISBN is looked up against library metadata and formatted APA 7 style — author, year, italicized title, and publisher — including edition information when the record carries it.",
    isbnToApaUseCase:
      "Fastest way to cite a stack of physical library books: flip each to the copyright page, type the ISBNs, paste the list here.",
    isbnToMlaTitle: "ISBN to MLA Citation Converter",
    isbnToMlaMetaDescription:
      "Convert ISBNs to MLA 9 works-cited entries in one paste. Free ISBN lookup with formatted output — no sign-up needed.",
    isbnToMlaIntro:
      "MLA 9 book entries are short — author, italicized title, publisher, year — but getting the author inversion and punctuation right still trips people up. An ISBN lookup returns the book's actual catalog record and formats it to the MLA handbook's core-elements template.",
    isbnToMlaUseCase:
      "Ideal for literature essays: cite every novel and critical monograph on your desk by typing nothing but the numbers on their back covers.",
    isbnToBibtexTitle: "ISBN to BibTeX Converter",
    isbnToBibtexMetaDescription:
      "Convert ISBNs into BibTeX @book entries for LaTeX. Paste one ISBN per line, download the .bib file — free and instant.",
    isbnToBibtexIntro:
      "Books are the awkward corner of BibTeX — no DOI to look up, and publisher pages rarely offer a .bib export. This converter resolves each ISBN against library metadata and emits @book entries with author, title, publisher, and year fields filled.",
    isbnToBibtexUseCase:
      "For thesis writers: the methods chapters cite journal DOIs, but the theory chapter cites twelve books. This handles the twelve books.",
    pmidToApaTitle: "PMID to APA Citation Converter",
    pmidToApaMetaDescription:
      "Convert PubMed IDs to APA 7 references automatically. Paste PMIDs one per line and get a formatted reference list — free.",
    pmidToApaIntro:
      "Every PubMed record has a PMID, and psychology and nursing programs overwhelmingly require APA. Paste PMIDs one per line and each resolves through PubMed's own metadata into a complete APA 7 journal reference, DOI link included when the record has one.",
    pmidToApaUseCase:
      "Built for evidence-based-practice assignments: your search strategy ends with a list of PMIDs, and your paper needs them in APA.",
    pmidToRisTitle: "PMID to RIS Converter",
    pmidToRisMetaDescription:
      "Turn PubMed IDs into a RIS file for Zotero, Mendeley, or EndNote. Paste PMIDs one per line, download the .ris — free.",
    pmidToRisIntro:
      "Systematic-review workflows live on RIS files. Paste the PMIDs from your PubMed search and download a .ris ready for your screening tool or reference manager — each record structured from PubMed's canonical metadata rather than scraped text.",
    pmidToRisUseCase:
      "Useful mid-review, when a colleague emails you 'also include these five studies' as bare PMIDs and your library lives in EndNote.",
    arxivToBibtexTitle: "arXiv to BibTeX Converter",
    arxivToBibtexMetaDescription:
      "Convert arXiv IDs to BibTeX entries for LaTeX papers. Paste IDs like 2301.12345, one per line, and download the .bib — free.",
    arxivToBibtexIntro:
      "Preprints get cited constantly in fast-moving fields, and arXiv IDs (like 2301.12345) are how they circulate. Paste the IDs and get BibTeX entries with the preprint's authors, title, year, and arXiv link — ready for your paper's .bib file.",
    arxivToBibtexUseCase:
      "For ML and physics writers whose related-work section is 80% arXiv: collect the IDs while reading, convert them in one paste at writing time.",
    bibtexToApaTitle: "BibTeX to APA Converter",
    bibtexToApaMetaDescription:
      "Paste or upload a .bib file and get an APA 7 reference list. Handles multi-entry files — free, runs the parse in your browser.",
    bibtexToApaIntro:
      "Sometimes the paper leaves LaTeX: a journal wants a Word submission, or a committee wants the reference list as plain text. Paste your BibTeX (or upload the .bib) and every entry is parsed in your browser, then formatted as an APA 7 reference list you can drop into any document.",
    bibtexToApaUseCase:
      "The grant-application special: your publication list lives in a .bib file, and the funder's form wants APA-formatted references in a text box.",
    bibtexToMlaTitle: "BibTeX to MLA Converter",
    bibtexToMlaMetaDescription:
      "Convert BibTeX to MLA 9 works-cited entries. Paste or upload the .bib file and copy the formatted list — free, no account.",
    bibtexToMlaIntro:
      "BibTeX stores raw fields; MLA prescribes exactly how to arrange them. This converter parses your .bib entries locally and renders each through the MLA 9 template — author inversion, quoted article titles, italicized containers, and correct punctuation between elements.",
    bibtexToMlaUseCase:
      "For crossover scholars: your references live in BibTeX from past LaTeX work, but the humanities journal you're submitting to wants MLA.",
    bibtexToRisTitle: "BibTeX to RIS Converter",
    bibtexToRisMetaDescription:
      "Convert a .bib file to RIS for Zotero, Mendeley, or EndNote. Paste or upload BibTeX, download the .ris — free and offline.",
    bibtexToRisIntro:
      "Moving from LaTeX tooling to a reference manager means translating BibTeX's @article/@book entries into RIS's TY/AU/TI tags. The conversion runs entirely in your browser — field mappings included — and hands you a .ris file any manager imports.",
    bibtexToRisUseCase:
      "Typical trigger: a new co-author works in Zotero, your shared references are a .bib in the repo, and someone has to bridge the gap.",
    bibtexToCslJsonTitle: "BibTeX to CSL-JSON Converter",
    bibtexToCslJsonMetaDescription:
      "Convert BibTeX to CSL-JSON for Pandoc and Quarto. Paste or upload the .bib, download references.json — free, in-browser.",
    bibtexToCslJsonIntro:
      "Pandoc and Quarto cite from CSL-JSON natively, and converting your existing .bib removes a moving part from the build. Entries parse in your browser and come out as a clean CSL-JSON array — the exact shape --bibliography=references.json expects.",
    bibtexToCslJsonUseCase:
      "For Markdown-first academics converting an old LaTeX project to Quarto: bring the bibliography along without hand-editing JSON.",
    bibtexToWordTitle: "BibTeX to Word Converter",
    bibtexToWordMetaDescription:
      "Turn a .bib file into a Word document with an APA-formatted, hanging-indent reference list. Free — parse happens in your browser.",
    bibtexToWordIntro:
      "When a .bib file needs to become a document — not another data format — this converter parses your BibTeX locally, formats every entry in APA 7, and downloads a Word file with the reference list already alphabetized and hanging-indented.",
    bibtexToWordUseCase:
      "End-of-pipeline move: the journal accepted the paper but wants the final reference list as an editable Word file, and your source of truth is BibTeX.",
    risToApaTitle: "RIS to APA Converter",
    risToApaMetaDescription:
      "Paste or upload a RIS export and get an APA 7 reference list. Works with Zotero, Mendeley, EndNote, and database exports — free.",
    risToApaIntro:
      "Every database's Export button produces RIS; assignments want APA. Paste the export (or upload the .ris) and each TY/AU/TI record is parsed in your browser and rendered as an APA 7 reference — no reference manager installation required.",
    risToApaUseCase:
      "The library-database shortcut: export your search results as RIS, convert here, and skip setting up Zotero for a one-off paper.",
    risToMlaTitle: "RIS to MLA Converter",
    risToMlaMetaDescription:
      "Convert RIS exports from databases or reference managers into MLA 9 works-cited entries. Paste or upload — free, no sign-up.",
    risToMlaIntro:
      "JSTOR, ProQuest, and EBSCO all export RIS, and English departments all want MLA. This page closes that gap: records parse locally and render through the MLA 9 core-elements template, ready to paste into your works-cited page.",
    risToMlaUseCase:
      "Made for the night before the essay is due: ten sources exported from JSTOR as citations.ris, zero desire to format them by hand.",
    risToBibtexTitle: "RIS to BibTeX Converter",
    risToBibtexMetaDescription:
      "Convert RIS to BibTeX for LaTeX and Overleaf. Paste or upload the .ris export, download a clean .bib file — free, in-browser.",
    risToBibtexIntro:
      "Going the LaTeX direction: reference managers and databases speak RIS, your paper's build needs .bib. Records convert entirely in your browser — types mapped from TY tags, cite keys generated — into a file Overleaf accepts as-is.",
    risToBibtexUseCase:
      "Common in collaborations: the Zotero user exports RIS, the LaTeX user runs it through this page, and both keep their tools.",
    risToCslJsonTitle: "RIS to CSL-JSON Converter",
    risToCslJsonMetaDescription:
      "Convert RIS exports to CSL-JSON for Pandoc and Quarto pipelines. Paste or upload the .ris, download references.json — free.",
    risToCslJsonIntro:
      "For Pandoc and Quarto users, CSL-JSON is the native bibliography format. Feed this page the RIS your database or manager exported and get back a structured JSON array — every record parsed locally, nothing uploaded anywhere.",
    risToCslJsonUseCase:
      "Slots into docs-as-code workflows: reviewers send RIS attachments, your repo's citation file is references.json, this page is the adapter.",
    cslJsonToBibtexTitle: "CSL-JSON to BibTeX Converter",
    cslJsonToBibtexMetaDescription:
      "Convert CSL-JSON (Pandoc/Quarto/Zotero exports) to BibTeX. Paste or upload the .json, download the .bib — free, in-browser.",
    cslJsonToBibtexIntro:
      "Sometimes the flow reverses: a bibliography born in a Pandoc or Zotero pipeline needs to feed a LaTeX build. This converter reads the CSL-JSON array in your browser and emits BibTeX entries with types and fields mapped back from the CSL model.",
    cslJsonToBibtexUseCase:
      "For the Quarto author whose co-author insists on Overleaf: keep references.json canonical and generate the .bib on demand.",
    cslJsonToRisTitle: "CSL-JSON to RIS Converter",
    cslJsonToRisMetaDescription:
      "Convert CSL-JSON to RIS so Zotero, Mendeley, or EndNote can import it. Paste or upload the .json, download the .ris — free.",
    cslJsonToRisIntro:
      "CSL-JSON is wonderful inside Pandoc pipelines and opaque to most reference managers' import dialogs. Converting to RIS makes the same records universally importable — each item mapped to TY/AU/TI tags in your browser, nothing sent to a server.",
    cslJsonToRisUseCase:
      "The hand-off problem: your bibliography is a references.json in a git repo, and your advisor wants it 'in EndNote' by Friday.",
  },

  // The infinite-scroll style list shared by the country and subject pages and by the
  // /citation-styles hub. It is a client component, so these arrive as a prop rather
  // than through `getDictionary` — pulling all 17 dictionaries into the browser bundle
  // to label a search box is the thing `link-path.ts` exists to avoid.
  stylesBrowser: {
    defaultTitle: "All Citation Styles",
    styleSingular: "style",
    stylePlural: "styles",
    found: "found",
    filterPlaceholder: "Filter styles...",
    noMatches: "No styles match your search.",
    loadingMore: "Loading more styles...",
    loadMore: "Load more ({count} remaining)",
    showingAll: "Showing all {count} styles",
    internationalLabel: "International",
  },

  footer: {
    tagline:
      "A free citation generator supporting {count} styles. No account, no paid subscription, no limits.",
    freeTools: "Free Tools",
    resources: "Resources",
    legal: "Legal",
    language: "Language",
    languageLabel: "Choose your language",
    rights:
      "Citations are formatted with the official Citation Style Language definitions.",
    popularStyles: "Popular Styles",
    about: "About",
    privacy: "Privacy",
    terms: "Terms",
    contact: "Contact",
    copyright: "© {year} CitationEasy. Free to use.",
    poweredBy: "Powered by",
    allTools: "All Writing Tools",
    citationGuides: "Citation Guides",
    allStyles: "All Citation Styles",
    citationGlossary: "Citation Glossary",
    howToCiteAny: "How to Cite Any Source",
  },

  // Questions and answers rendered on the localized home and style pages, and
  // emitted as FAQPage structured data alongside them. Google requires the markup to
  // match visible text, so these two always ship together — see
  // components/i18n/faq-section.tsx.
  faq: {
    heading: "Frequently Asked Questions",
    homeQ1: "Is CitationEasy really free?",
    homeA1:
      "Yes. CitationEasy is completely free, with no sign-up, no limits, and no paid tier. Every one of the {count} citation styles is available to everyone.",
    homeQ2: "Which citation styles are supported?",
    homeA2:
      "All {count} styles from the Citation Style Language project, including APA 7th edition, MLA 9th edition, Chicago, Harvard, Vancouver, IEEE, AMA, and Turabian.",
    homeQ3: "Do I need an account?",
    homeA3:
      "No. CitationEasy runs entirely in your browser and your bibliography is stored on your own device. Nothing is uploaded and there is nothing to sign up for.",
    homeQ4: "Can I write my citations in my own language?",
    homeA4:
      "Yes. The citation language is a separate setting from the site language, so you can work in your language while producing references in the language your paper is written in.",
    styleQ1: "Is the {style} citation generator free?",
    styleA1:
      "Yes. Generating {style} citations is free and unlimited, with no account required. Paste a DOI, URL, ISBN, or BibTeX entry and copy the finished reference.",
    styleQ2: "How do I cite a source in {style}?",
    styleA2:
      "Paste the source's DOI, URL, ISBN, or BibTeX entry into the generator on this page. {style} is already selected, so the formatted reference and its in-text citation appear straight away.",
    styleQ3: "How accurate is the {style} formatting?",
    styleA3:
      "Every reference is built from the official {style} definition published by the Citation Style Language project — the same file reference managers such as Zotero and Mendeley use — rather than from an approximation of the rules.",
  },

  // The generator's own UI. Read through a React context (see
  // components/citation-tool/tool-strings.tsx) rather than prop-drilled through nine
  // components; English is the default, so English pages pass nothing.
  tool: {
    modeLabel: "Citation input mode",
    modeAuto: "URL / DOI / ISBN",
    modeBook: "Search Books",
    modeArticles: "Search Articles",
    modeManual: "Manual Entry",
    // Offered under the error banner when a source page could not be read (a
    // paywall or bot wall answering 401/403, a dead link, a site that timed out).
    // Switches to Manual Entry with the address already filled in.
    errorEnterManually: "Enter the details manually",
    autoLabel: "Paste URL, DOI, PMID, arXiv ID, ISBN, or BibTeX",
    autoHint:
      "We'll automatically detect the type and fetch metadata. Paste one identifier per line to cite a whole list at once.",
    generate: "Generate Citation",
    generating: "Generating...",
    bookSearchLabel: "Search by title, author, or ISBN",
    bookSearchEmpty: "No results yet. Click Search to find books.",
    bookSearchSource: "Searches Open Library's catalog of millions of books",
    worksSearchLabel: "Search by article title or authors",
    worksSearchEmpty: "No results yet. Click Search to find articles.",
    worksSearchSource:
      "Searches Crossref's catalog of journal articles, conference papers, and book chapters",
    showingResults: "Showing {shown} of {total} results",
    search: "Search",
    searching: "Searching...",
    bulkResults: "Bulk citation results",
    copy: "Copy",
    copied: "Copied!",
    copyHint: "Copy and paste into your paper",
    resultTitle: "Formatted Citation",
    edit: "Edit",
    completeFields: "Complete",
    fieldsIncomplete:
      "one:{count} field incomplete|other:{count} fields incomplete",
    outputLanguage: "Language",
    sourceType: "Source type",
    fieldAuthors: "Author(s)",
    fieldAuthorsHint: "Smith, John or John Smith",
    fieldTitle: "Article or book title",
    fieldBookTitle: "Book title",
    fieldBookTitleHint: "Title of the book or anthology",
    fieldEditors: "Editor(s)",
    fieldJournal: "Journal / Publication",
    fieldJournalHint: "Journal or publication name",
    fieldPublisher: "Publisher / Site",
    fieldPublisherHint: "Publisher name or website",
    fieldDate: "Date / Year",
    fieldVolume: "Volume",
    fieldIssue: "Issue",
    fieldPages: "Page(s)",
    fieldFormat: "Format / medium",
    fieldAuthorsGroup: "Group or organization author",
    fieldAuthorsGroupHint:
      "To list a group with people, wrap it in braces: {R Core Team}, Jane Doe",
    fieldTranslators: "Translator(s)",
    fieldInterviewers: "Interviewer(s)",
    fieldVersion: "Version",
    fieldCourt: "Court",
    fieldIssuingAuthority: "Issuing authority",
    fieldPatentNumber: "Patent number",
    fieldEpisodeNumber: "Episode number",
    fieldLawNumber: "Public law or bill number",
    fieldReportNumber: "Report number",
    fieldReviewedTitle: "Title of the reviewed work",
    fieldReviewedAuthors: "Author(s) of the reviewed work",
    inTextCitation: "In-text citation",
    quickEdit: "Quick Edit",
    quickEditHint: "Update the metadata and regenerate the citation.",
    update: "Update",
    saving: "Saving...",
    fieldAuthor: "Author",
    fieldDateShort: "Date",
    fieldTitleShort: "Title",
  },

  // The saved-bibliography flow: the dialog, the save button, notes, in-text copy, and
  // reformatting. Read through the same context as `tool` — see
  // components/citation-tool/tool-strings.tsx.
  // ---- /tools hub: the shelf names, one per `ToolCollection` (src/data/tool-taxonomy.ts) ----
  toolCollections: {
    "citation-generators": "Citation Generators",
    "citation-tools": "Citation & Reference Tools",
    "paper-formatting": "Paper Formatting",
    "essay-writing": "Essay & Paper Writing",
    "writing-analysis": "Writing Analysis & Editing",
    "word-counters": "Counters & Readability",
    vocabulary: "Vocabulary & Word Finders",
    "research-tools": "Research & Publishing",
    statistics: "Statistics Calculators",
    math: "Math Calculators",
    science: "Science & Lab Calculators",
    charts: "Charts & Diagrams",
    economics: "Economics, Business & Finance",
    grades: "GPA & Grade Calculators",
    study: "Study & Productivity",
    classroom: "Classroom & Printables",
    random: "Random Generators & Pickers",
    "text-tools": "Text Cleanup & Formatting",
    "fancy-text": "Fancy Text & Symbols",
    language: "Language & Transliteration",
    ciphers: "Codes & Ciphers",
    converters: "Unit, Date & Number Converters",
    developer: "Data, Markup & Code",
  },
  bib: {
    title: "My Bibliographies",
    studioTitle: "Bibliography Studio",
    studioProjects: "Projects",
    studioExplore: "Explore",
    studioExploreIntro:
      "Guides, tools, and every citation style are one click away.",
    studioStyleGuide: "{style} style guide",
    studioStyleGuideHint: "Rules, worked examples, and the in-text format",
    studioGenerator: "Citation generator",
    studioNewProject: "New project",
    studioSwitchProject: "Switch project",
    studioAddSource: "Add a source",
    studioAddSourceHint:
      "Paste a DOI, URL, or ISBN, search books and articles, or enter details by hand. Everything you save lands in this project.",
    studioDoneAdding: "Done adding",
    studioReferences: "References",
    studioReferenceCount: "{count} references",
    studioInspectorTitle: "Reference details",
    studioInspectorEmpty:
      "Select a reference to see its in-text citation, notes, and source details.",
    studioSavedLocally: "Saved in this browser",
    studioOpen: "Open Bibliography Studio",
    studioViewInStudio: "View in Studio",
    studioFooterSaved: "{count} saved in your Bibliography Studio",
    studioFooterEmpty:
      "Saved citations build a full bibliography in the Studio",
    studioClearSelection: "Clear selection",
    studioDeleteSelected: "Delete selected",
    studioDeleteSelectedBody:
      "Delete {count} citations from “{name}”? This cannot be undone.",
    studioDeletedMany: "{count} citations deleted",
    studioMoveTo: "Move to",
    studioCopyTo: "Copy to",
    studioSearchShortcut: "Press / to search",
    studioStyle: "Style",
    studioCopyEntry: "Copy entry",
    studioEntryCopied: "Entry copied",
    studioSourceDetails: "Source details",
    studioEmptyTitle: "This project is empty",
    studioEmptyBody:
      "Add your first source above, or import an existing BibTeX, RIS, or CSL-JSON library.",
    studioBackup: "Backup",
    sharedTitle: "Shared Bibliography",
    save: "Save",
    saving: "Saving…",
    saved: "Saved",
    searchPlaceholder: "Search citations…",
    empty: "No citations saved yet",
    emptySearch: "No citations match your search.",
    countIn: "Citations in {name}: {count}",
    savedOn: "Saved {date}",
    emptyInBib:
      "No citations in this bibliography yet. Generate and save citations, or import an existing BibTeX, RIS, or CSL-JSON library.",
    savedToast: "Citation saved to bibliography",
    copyAll: "Copy All",
    copyAllTitle: "Copy all citations",
    export: "Export",
    exportTitle: "Export bibliography",
    import: "Import",
    importTitle: "Import references",
    importLabel: "Import references (BibTeX, RIS, or CSL-JSON)",
    importing: "Importing…",
    importFailed: "Import failed. Please try again.",
    importSummary:
      "Imported: {added} · Duplicates skipped: {duplicates} · Over limit: {overLimit} · Failed: {failed}",
    importUnreadable: "Could not read file.",
    importUnparseable: "Could not import references.",
    importReviewTitle: "Review import",
    importReviewIntro:
      "Review {total} {format} references before formatting and saving them.",
    importReviewCount: "{included} of {total} included",
    importSelectAll: "Include all",
    importExcludeAll: "Exclude all",
    importLimitNotice:
      "{count} references over the 200-entry limit will not be imported.",
    importDuplicateExisting: "Possible duplicate of “{title}” already saved",
    importDuplicateBatch: "Possible duplicate of “{title}” in this import",
    importMissingField: "Missing {field}",
    importUntitled: "Untitled reference",
    importReview: "Review references",
    importCommit: "Format and import",
    importBack: "Back",
    importOutcomeTitle: "Import complete",
    importOutcomeSummary:
      "Added: {added} · Duplicates skipped: {duplicates} · Excluded: {excluded} · Over limit: {overLimit} · Failed: {failed}",
    sortBy: "Sort by",
    sortTitle: "Sort bibliography",
    sortAuthor: "Author (A–Z)",
    sortNewest: "Date added (newest)",
    sortOldest: "Year (oldest first)",
    sortManual: "Manual order",
    backupAll: "Full project backup (.json)",
    backupDownloaded: "Full project backup downloaded",
    backupUnreadable: "Could not read the backup file.",
    restore: "Restore",
    restoreTitle: "Restore all bibliography projects",
    restoreWarning:
      "This validates the backup first, then replaces every bibliography currently stored in this browser. This cannot be undone.",
    restoreChoose: "Choose a CitationEasy backup (.json)",
    replaceAndRestore: "Replace and restore",
    restoreComplete: "All bibliography projects restored",
    restoreInvalid: "This is not a valid CitationEasy project backup.",
    selectAllVisible: "Select all shown",
    selectedCount: "{count} selected",
    clusterOpen: "Cite together",
    clusterTitle: "Composite in-text citation",
    clusterDescription:
      "Build one citation cluster from the selected references. The chosen style controls the ordering, punctuation, and numbering.",
    clusterTarget: "Output",
    clusterLocatorOptional: "Optional locator",
    clusterBuildCopy: "Build and copy citation",
    clusterBuilding: "Building citation…",
    clusterCopied: "Composite citation copied",
    clusterFailed: "Could not build the composite citation. Please try again.",
    destination: "Destination bibliography",
    chooseDestination: "Choose a bibliography…",
    copySelected: "Copy",
    moveSelected: "Move",
    copyComplete:
      "Copied {count} citations. {skipped} duplicates were skipped.",
    moveComplete:
      "Moved {count} citations. {skipped} duplicates stayed in the source.",
    selectCitation: "Select {title}",
    moveUp: "Move up",
    moveDown: "Move down",
    deleteTitle: "Delete citation",
    deleteConfirm: "Delete citation?",
    deleted: "Citation deleted",
    editTitle: "Edit citation",
    close: "Close",
    copiedAll: "All citations copied!",
    copiedAnnotated: "Annotated bibliography copied!",
    copiedBibtex: "BibTeX copied!",
    copiedBulleted: "Bulleted list copied!",
    copiedNumbered: "Numbered list copied!",
    copiedHtml: "HTML snippet copied!",
    downloadedWord: "Word document downloaded",
    downloadedRtf: "RTF file downloaded",
    downloadedRis: "RIS file downloaded",
    downloadedCsl: "CSL-JSON file downloaded",
    downloadedAnnotated: "Annotated bibliography downloaded",
    copyFailed: "Failed to copy to clipboard",
    shareCopied:
      "Share link copied! Anyone with it can view and import this list.",
    shareFailed: "Could not create the share link",
    regenerateFailed: "Failed to regenerate citation",
    updateFailed: "Failed to update citation",
    fieldAuthor: "Author",
    fieldTitle: "Title",
    fieldDate: "Date",
    fieldPublisher: "Publisher",
    fieldVolume: "Volume",
    fieldIssue: "Issue",
    fieldPages: "Page(s)",
    fieldUrl: "URL",
    fieldAccessed: "Access date",
    fieldEditor: "Editor",
    fieldEvent: "Event location",
    // Notes / annotated bibliography
    noteLabel: "Annotation for this source",
    noteEdit: "Edit this note",
    notePlaceholder:
      "Summarise the source, assess it, and say how it fits your project…",
    noteRemoved: "Note removed",
    noteSave: "Save note",
    noteCancel: "Cancel",
    noteAdd: "Add note",
    noteWords: "{count} words",
    noteSaved: "Note saved ({count} words)",
    noteClickToEdit: "click to edit",
    // In-text citation popover
    inTextCopy: "Copy the in-text citation for this source",
    inTextSelect: "Select the citation below to copy it.",
    inTextFailed: "Could not build the in-text citation. Please try again.",
    locator: "Locator",
    locatorType: "Locator type",
    locatorPage: "Page",
    locatorChapter: "Chapter",
    locatorSection: "Section",
    locatorParagraph: "Paragraph",
    locatorLine: "Line",
    locatorVerse: "Verse",
    // Reformat
    reformat: "Reformat",
    reformatTitle: "Reformat every citation in a different style",
    reformatHeading: "Reformat everything in one style",
    reformatChoose: "Choose a style…",
    reformatFailed: "Reformatting failed. Your bibliography is unchanged.",
    sortOrder: "Sort order",
    copyToClipboard: "Copy to clipboard",
    numberedList: "Numbered list",
    bulletedList: "Bulleted list",
    htmlSnippet: "HTML snippet",
    copyWithAnnotations: "Copy with annotations",
    wordAnnotated: "Word document, annotated (.doc)",
    downloadFile: "Download file",
    wordDocument: "Word document (.doc)",
    copyShareLink: "Copy share link",
    print: "Print…",
    printFailed: "Pop-up blocked — allow pop-ups to print",
    font: "Font",
    sitePublication: "Site / publication name",
    importIntro:
      "Paste or upload a BibTeX (.bib), RIS (.ris), or CSL-JSON (.json)",
    importUpload: "Upload a .bib, .ris, or .json file",
    importPaste: "Or paste BibTeX, RIS, or CSL-JSON",
    cancel: "Cancel",
    done: "Done",
    delete: "Delete",
    deleteBody:
      "This will permanently remove this citation from your bibliography.",
    editBody: "Update the metadata and save to regenerate the citation.",
    reformatExplain:
      "Rebuilds all {count} entries from their saved source data — nothing is re-downloaded and no source is lost.",
    inTextHeading: "In-text citation",
    inTextLocatorHint:
      "Add a page or other locator to pin the citation to a spot in the source — required by most styles when you quote directly. Leave it blank for the plain form.",
    inTextCopyButton: "Copy in-text citation",
    mixedStyles: "This bibliography mixes {count} citation styles ({styles}).",
    mixedStylesAction:
      "Use {action} to put them all in one style before you hand it in.",
    selectBibliography: "Select bibliography",
    newBibliography: "New bibliography",
    moreOptions: "More options",
    rename: "Rename",
    removeDuplicates: "Remove duplicates",
    cannotDeleteOnly: "You cannot delete your only bibliography",
    newBibliographyBody:
      "Create a bibliography for a different paper or project.",
    bibliographyName: "Bibliography name",
    create: "Create",
    renameBibliography: "Rename bibliography",
    renameBibliographyBody: "Enter a new name for “{name}”.",
    newName: "New name",
    duplicateRemovedOne: "Removed {count} duplicate",
    duplicateRemovedMany: "Removed {count} duplicates",
    noDuplicates: "No duplicate references found",
    removeDuplicatesConfirm: "Remove duplicate references?",
    removeDuplicatesBody:
      "This scans “{name}” for the same source (matched by DOI, ISBN, or title, year, and author) and keeps one copy. References that cannot be matched are left untouched.",
    deleteBibliographyConfirm: "Delete bibliography?",
    deleteBibliographyBody:
      "This will permanently delete “{name}” and all {count} citations in it.",
  },

  bibliographyPage: {
    metaTitle: "Bibliography Studio - Organize, Annotate & Export Citations",
    metaDescription:
      "Build and manage private bibliography projects in your browser. Search, sort, annotate, edit, reformat, import, export, and share citations without an account.",
    keyword1: "bibliography maker",
    keyword2: "citation manager",
    keyword3: "annotated bibliography tool",
    ogTitle: "Bibliography Studio",
    ogDescription:
      "A private, full-page workspace for organizing, formatting, annotating, importing, and exporting citations.",
    ogImageTitle: "Bibliography Studio",
    ogImageSubtitle:
      "Organize, annotate, reformat, export, and share your references.",
    ogKind: "Citation workspace",
    breadcrumb: "Bibliography Studio",
    eyebrow: "Private, browser-based workspace",
    lead: "Manage every reference for a paper in one place. Your projects stay in this browser unless you deliberately export or share them.",
    privacyTitle: "Private by default",
    privacyBody:
      "Projects and annotations use local browser storage. Share links include citations but intentionally leave out private notes.",
    workflowTitle: "From source list to final paper",
    workflowBody:
      "Switch projects, find and sort references, add annotations or in-text locators, correct source metadata, and reformat the entire list in one citation style.",
    formatsTitle: "Bring references in and take them anywhere",
    formatsBody:
      "Import BibTeX, RIS, or CSL-JSON. Copy formatted lists or export Word, RTF, RIS, CSL-JSON, HTML, BibTeX, and annotated versions.",
  },

  // The 95-term glossary: /glossary and /glossary/[slug].
  //
  // The chrome only. Each term's own prose — `short`, the `body` paragraphs, the
  // example — is ~10,600 words that cannot live in a flat string map and must not be
  // copied verbatim into sixteen files; it lives in `src/content/glossary/<locale>.json`
  // and is read through `lib/localized-content.ts`.
  glossaryPage: {
    metaTitle: "{term} - Definition and Examples",
    notFoundTitle: "Term not found",
    keywordDefinition: "{term} definition",
    keywordWhatIs: "what is {term}",
    ogTitle: "{term} - CitationEasy glossary",
    ogKind: "Citation glossary",
    breadcrumb: "Glossary",
    alsoCalled: "Also called: {aliases}",
    doItHeading: "Do it on CitationEasy",
    seeAlsoHeading: "See also",
    moreInHeading: "More in {category}",
    navLabel: "Glossary navigation",
    allTermsLink: "All {count} glossary terms",
    generatorLink: "Free citation generator",
    termSetName: "CitationEasy citation glossary",
    hubMetaTitle: "Citation Glossary - {count} Terms Explained",
    hubMetaDescription:
      "Plain-English definitions of {count} citation and academic-writing terms: et al., ibid., DOI, hanging indent, patchwriting, and more, each with examples.",
    hubKeyword1: "citation glossary",
    hubKeyword2: "citation terms",
    hubKeyword3: "academic writing glossary",
    hubKeyword4: "what does et al mean",
    hubKeyword5: "what is a DOI",
    hubKeyword6: "referencing terminology",
    hubOgTitle: "Citation glossary",
    hubOgDescription:
      "Definitions of {count} citation and academic-writing terms, with examples.",
    hubOgImageTitle: "Citation Glossary",
    hubOgImageSubtitle:
      "{count} citation and academic-writing terms, defined in plain English with examples.",
    hubOgKind: "Reference",
    hubTermSetDescription:
      "Definitions of {count} citation, referencing, and academic-writing terms.",
    hubCollectionName: "Citation glossary",
    hubCollectionDescription:
      "Plain-English definitions of {count} citation and academic-writing terms.",
    hubHeading: "Citation glossary",
    hubIntro:
      "{count} terms from citation and academic writing, defined in plain English with examples — the abbreviations that show up in style guides, the parts a reference is built from, the identifiers that pin a source down, and the vocabulary of scholarly publishing.",
    hubJumpLabel: "Jump to letter",
    hubBrowseHeading: "Browse by topic",
    hubAllHeading: "All terms A–Z",
    hubWorkHeading: "Put the terms to work",
    hubWorkGenerator: "Free citation generator",
    hubWorkStyles: "Citation style guides",
    hubWorkHowTo: "How to cite any source",
    hubWorkCompare: "Compare citation styles",
    hubWorkTools: "Writing and citation tools",
    hubWorkFaq: "Citation FAQ",
  },

  // /faq — the 17-question hub. The questions and answers themselves are in
  // `src/data/faq.ts` (the one source the API, the MCP tool and /md also read); this is
  // the page around them.
  faqPage: {
    metaTitle: "Citation FAQ - Quick Answers to Common Citation Questions",
    metaDescription:
      "Fast, accurate answers to common citation questions: DOIs, no-author sources, et al., hanging indents, citing AI, plagiarism rules, and APA vs MLA.",
    ogTitle: "Citation FAQ",
    ogDescription:
      "Quick answers to the most common citation questions, from DOIs and et al. to citing ChatGPT.",
    ogImageTitle: "Citation Questions and Answers",
    ogImageSubtitle:
      "Short, accurate answers to the questions students actually ask about citing.",
    ogKind: "FAQ",
    breadcrumbLabel: "Breadcrumb",
    breadcrumbHome: "Home",
    breadcrumbCurrent: "Citation FAQ",
    heading: "Citation FAQ",
    intro:
      "Quick, accurate answers to the citation questions students actually ask — with links to the full guide or tool when you need more depth.",
    contentsLabel: "FAQ contents",
    stillStuckHeading: "Still stuck?",
    stillStuckBody:
      "Browse the [full guide library](/guides), check the [style cheat sheets](/cheat-sheets), look a term up in the [citation glossary](/glossary), [search the whole site](/search), or just [generate the citation](/) and let the formatter apply the rules for you.",
  },

  // /cheat-sheets and its three style sheets. The citation FORMATS in them
  // ("Author, A. A. (Year)…") are the styles' own templates and stay as they are in
  // every language, exactly as the per-style examples do; the labels around them do not.
  cheatSheets: {
    hubMetaTitle: "Citation Style Cheat Sheets - Quick Reference Guides",
    hubMetaDescription:
      "Quick-reference cheat sheets for APA, MLA, and Chicago citation styles. Print-friendly guides with common source type examples.",
    hubBreadcrumbHome: "Home",
    hubBreadcrumbCurrent: "Citation Style Cheat Sheets",
    hubBackLink: "← Back to Generator",
    hubHeading: "Citation Style Cheat Sheets",
    hubIntro:
      "Quick-reference guides for the most popular citation styles. Print-friendly and always up to date.",
    hubApaTitle: "APA 7th Edition Cheat Sheet",
    hubApaDesc: "Psychology, social sciences, education, nursing",
    hubMlaTitle: "MLA 9th Edition Cheat Sheet",
    hubMlaDesc: "Literature, languages, humanities, cultural studies",
    hubChicagoTitle: "Chicago Style Cheat Sheet",
    hubChicagoDesc: "History, art history, philosophy, religion",
    backToSheets: "← Back to Cheat Sheets",
    generalRules: "General Rules",
    inTextCitations: "In-Text Citations",
    colSituation: "Situation",
    colFormat: "Format",
    colExample: "Example",
    book: "Book",
    journalArticle: "Journal Article",
    website: "Website",
    apaMetaTitle: "APA 7th Edition Cheat Sheet - Quick Reference Guide",
    apaMetaDescription:
      "APA 7th edition citation cheat sheet with examples for books, journals, websites, and more. Print-friendly quick reference.",
    apaHeading: "APA 7th Edition Cheat Sheet",
    apaLead: "Quick reference for the most common APA citation formats.",
    apaRule1:
      'Use **hanging indentation** (first line flush left, subsequent lines indented 0.5")',
    apaRule2: "Double-space all entries",
    apaRule3: "Alphabetize by author's last name",
    apaRule4: "Italicize titles of books, journals, and volumes",
    apaRule5: "Use DOI when available",
    apaRow1Situation: "1 author",
    apaRow1Format: "(Author, Year)",
    apaRow1Example: "(Smith, 2024)",
    apaRow2Situation: "2 authors",
    apaRow2Format: "(Author & Author, Year)",
    apaRow2Example: "(Smith & Jones, 2024)",
    apaRow3Situation: "3+ authors",
    apaRow3Format: "(First Author et al., Year)",
    apaRow3Example: "(Smith et al., 2024)",
    apaRow4Situation: "Direct quote",
    apaRow4Format: "(Author, Year, p. #)",
    apaRow4Example: "(Smith, 2024, p. 45)",
    apaRow5Situation: "No author",
    apaRow5Format: '("Title," Year)',
    apaRow5Example: '("Study Results," 2024)',
    apaReferenceHeading: "Reference List Examples",
    apaBookExample:
      "Author, A. A. (Year). *Title of work: Capital letter also for subtitle*. Publisher. https://doi.org/xxxxx",
    apaJournalExample:
      "Author, A. A., & Author, B. B. (Year). Title of article. *Title of Periodical, Volume*(Issue), Page–Page. https://doi.org/xxxxx",
    apaWebsiteExample:
      "Author, A. A. (Year, Month Day). *Title of page*. Site Name. https://www.example.com/page",
    apaChapterLabel: "Edited Book Chapter",
    apaChapterExample:
      "Author, A. A. (Year). Title of chapter. In E. E. Editor (Ed.), *Title of book* (pp. #–#). Publisher.",
    apaConferenceLabel: "Conference Paper",
    apaConferenceExample:
      "Author, A. A. (Year, Month Days). *Title of paper* [Paper presentation]. Conference Name, Location.",
    apaDifferencesHeading: "Key Differences from APA 6th",
    apaDifference1: 'No "Retrieved from" before URLs',
    apaDifference2: "Include DOI as https://doi.org/ link format",
    apaDifference3: "Up to 20 authors listed before using ellipsis",
    apaDifference4: "Publisher location no longer required",
    apaDifference5: 'Use singular "they" for gender-neutral language',
    apaCtaText: "Generate perfect APA citations automatically",
    apaCtaButton: "APA Citation Generator →",
    mlaMetaTitle: "MLA 9th Edition Cheat Sheet - Quick Reference Guide",
    mlaMetaDescription:
      "MLA 9th edition citation cheat sheet with examples for books, articles, websites, and more. Print-friendly quick reference.",
    mlaHeading: "MLA 9th Edition Cheat Sheet",
    mlaLead: "Quick reference for the most common MLA citation formats.",
    mlaRule1: "Use **hanging indentation**",
    mlaRule2: "Double-space all entries",
    mlaRule3: "Alphabetize by author's last name",
    mlaRule4: 'Title the page "Works Cited"',
    mlaRule5: "Italicize titles of containers (books, journals, websites)",
    mlaRow1Situation: "1 author",
    mlaRow1Format: "(Author Page)",
    mlaRow1Example: "(Smith 45)",
    mlaRow2Situation: "2 authors",
    mlaRow2Format: "(Author and Author Page)",
    mlaRow2Example: "(Smith and Jones 45)",
    mlaRow3Situation: "3+ authors",
    mlaRow3Format: "(First Author et al. Page)",
    mlaRow3Example: "(Smith et al. 45)",
    mlaRow4Situation: "No author",
    mlaRow4Format: '("Title" Page)',
    mlaRow4Example: '("Study Results" 45)',
    mlaRow5Situation: "No page number",
    mlaRow5Format: "(Author)",
    mlaRow5Example: "(Smith)",
    mlaWorksCitedHeading: "Works Cited Examples",
    mlaBookExample: "Last, First. *Title of Book*. Publisher, Year.",
    mlaJournalExample:
      'Last, First. "Title of Article." *Journal Title*, vol. #, no. #, Year, pp. #–#.',
    mlaWebsiteExample:
      'Last, First. "Title of Page." *Website Name*, Day Month Year, URL.',
    mlaAnthologyLabel: "Anthology/Edited Book",
    mlaAnthologyExample:
      'Last, First. "Title of Essay." *Title of Collection*, edited by First Last, Publisher, Year, pp. #–#.',
    mlaCoreHeading: "MLA Core Elements (in order)",
    mlaCore1: "Author.",
    mlaCore2: '"Title of Source."',
    mlaCore3: "*Title of Container*,",
    mlaCore4: "Contributors,",
    mlaCore5: "Version,",
    mlaCore6: "Number,",
    mlaCore7: "Publisher,",
    mlaCore8: "Publication date,",
    mlaCore9: "Location (pages, URL, DOI).",
    mlaCtaText: "Generate perfect MLA citations automatically",
    mlaCtaButton: "MLA Citation Generator →",
    chicagoMetaTitle: "Chicago Style Cheat Sheet - Quick Reference Guide",
    chicagoMetaDescription:
      "Chicago Manual of Style citation cheat sheet with Notes-Bibliography and Author-Date examples. Print-friendly quick reference.",
    chicagoHeading: "Chicago Style Cheat Sheet",
    chicagoLead:
      "Quick reference for Chicago Manual of Style (18th edition) citation formats.",
    chicagoSystemsHeading: "Two Chicago Systems",
    chicagoColNb: "Notes-Bibliography (NB)",
    chicagoColAd: "Author-Date (AD)",
    chicagoRow1Nb: "Footnotes/endnotes + bibliography",
    chicagoRow1Ad: "Parenthetical citations + reference list",
    chicagoRow2Nb: "History, arts, humanities",
    chicagoRow2Ad: "Sciences, social sciences",
    chicagoRow3Nb: "More detailed first notes",
    chicagoRow3Ad: "Similar to APA format",
    chicagoExamplesHeading: "Notes-Bibliography Examples",
    chicagoNoteLabel: "Note:",
    chicagoBibliographyLabel: "Bibliography:",
    chicagoBookLabel: "Book (Footnote)",
    chicagoBookNote: "1. First Last, *Title of Book* (Publisher, Year), page.",
    chicagoBookBibliography: "Last, First. *Title of Book*. Publisher, Year.",
    chicagoJournalLabel: "Journal Article (Footnote)",
    chicagoJournalNote:
      '1. First Last, "Title of Article," *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoJournalBibliography:
      'Last, First. "Title of Article." *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoWebsiteLabel: "Website (Footnote)",
    chicagoWebsiteNote:
      '1. First Last, "Title of Page," Site Name, Month Day, Year, URL.',
    chicagoWebsiteBibliography:
      'Last, First. "Title of Page." Site Name. Month Day, Year. URL.',
    chicagoShortHeading: "Shortened Notes",
    chicagoShortIntro:
      "After the first full citation, use shortened footnotes:",
    chicagoShortExample: "1. Last, *Short Title*, page.",
    chicagoRulesHeading: "Key Formatting Rules",
    chicagoRule1: "Use superscript numbers for footnote references in text",
    chicagoRule2: "Bibliography entries use hanging indentation",
    chicagoRule3:
      'Use a shortened note rather than "Ibid." for consecutive citations of the same source',
    chicagoRule4:
      "Titles: books and journals italicized, articles and chapters in quotes",
    chicagoRule5: "Use access dates for online sources that may change",
    chicagoCtaText: "Generate perfect Chicago citations automatically",
    chicagoCtaButton: "Chicago Citation Generator →",
  },

  // The catalog's page-type labels (`CATALOG_TYPE_LABELS`), which the search page shows
  // as facet chips and beside every result. Layer 2: a fact from `src/lib/**` rather
  // than page chrome, and English until it is mapped through here.
  catalogTypes: {
    core: "Core pages",
    styleLanding: "Major citation style hubs",
    style: "Citation style reference pages",
    styleExamples: "Citation example pages",
    styleFormat: "Paper formatting guides",
    styleInText: "In-text citation guides",
    styleAnnotatedBibliography: "Annotated bibliography guides",
    styleReferencePage: "Reference/works-cited page guides",
    tool: "Free writing & citation tools",
    guide: "Educational guides",
    guideCategory: "Guide categories",
    toolCollection: "Tool collections",
    howToCite: "How to cite <source> in <style>",
    comparison: "Citation style comparisons",
    converter: "Identifier & format converters",
    field: "Citation styles by academic field",
    region: "Citation styles by country/region",
    category: "Citation styles by subject category",
    cheatSheet: "One-page cheat sheets",
    glossary: "Citation and academic-writing glossary",
  },

  // /about
  aboutPage: {
    metaTitle: "About CitationEasy - Free Citation Generator",
    metaDescription:
      "CitationEasy is a free citation generator with {count} styles, built by Samuel Sergi on open-source tools: Citation Style Language, Citation.js, and Next.js.",
    backLink: "← Back to Generator",
    heading: "About CitationEasy",
    intro:
      "CitationEasy is a free, open-access citation generator built for students, researchers, and academic writers. It supports **{count} citation styles** and provides a growing collection of writing tools, educational guides, and reference resources — all without requiring a sign-up or subscription.",
    offerHeading: "What We Offer",
    offer1:
      "[Citation generator](/) with {count} styles including APA 7th, MLA 9th, Chicago 18th, Harvard, Vancouver, IEEE, and thousands more",
    offer2:
      "[40 free writing and citation tools](/tools) — word counter, readability checker, DOI validator, bibliography alphabetizer, and more",
    offer3:
      "[750 how-to-cite guides](/how-to-cite) covering every major source type across 6 citation styles",
    offer4:
      "[100+ educational guides](/guides) on citation best practices, academic writing, and research skills",
    offer5:
      "[Style comparison tool](/compare), [citation style quiz](/quiz), and [cheat sheets](/cheat-sheets) for quick reference",
    offer6:
      "7 export formats: plain text, BibTeX, Word, numbered list, bulleted list, RIS, and CSV",
    openSourceHeading: "Built With Open Source",
    openSourceIntro:
      "CitationEasy is made possible by a number of excellent open-source projects and APIs. We believe in giving credit where it's due:",
    openSource1:
      "[Citation Style Language (CSL)](https://citationstyles.org/) — the open standard that powers our {count} citation styles. CSL is maintained by a community of volunteers and used by reference managers like Zotero, Mendeley, and Papers. The style definitions come from the [CSL Styles Repository](https://github.com/citation-style-language/styles) on GitHub.",
    openSource2:
      "[Citation.js](https://citation.js.org/) — the JavaScript library that processes citations and formats them according to CSL styles. Created by Lars Willighagen.",
    openSource3:
      "[Open Library API](https://openlibrary.org/developers/api) — the free book search API provided by the Internet Archive, used for looking up book metadata by title or ISBN.",
    openSource4:
      "[DOI Foundation](https://www.doi.org/) — the Digital Object Identifier system used for resolving and validating DOIs.",
    createdHeading: "Created By",
    createdBody:
      "CitationEasy was created and is maintained by **Samuel Sergi**. The goal is simple: provide a genuinely free, high-quality citation tool that doesn't gate features behind paywalls or require an account.",
    contactPrompt: "Have questions, feedback, or suggestions? Get in touch:",
    contactEmail: "Email: [sam@sammysergi.com](mailto:sam@sammysergi.com)",
    missionHeading: "Our Mission",
    mission1:
      "Accurate citations shouldn't cost money. Many citation tools advertise as free but restrict export formats, limit the number of citations, or push users toward premium plans. CitationEasy is committed to being completely free — no sign-up, no limits, no premium tier.",
    mission2:
      "Every feature on this site, from the citation generator to the writing tools and educational guides, is available to everyone at no cost. We believe that academic integrity tools should be accessible to all students and researchers, regardless of their budget.",
  },

  // /contact
  contactPage: {
    metaTitle: "Contact Us",
    metaDescription:
      "Get in touch with CitationEasy. Questions, feedback, or suggestions about our free citation generator and writing tools? We'd love to hear from you.",
    backLink: "← Back to Generator",
    heading: "Contact Us",
    intro:
      "Have questions, feedback, or suggestions about CitationEasy? We'd love to hear from you. Whether you've found a bug, have a feature request, or need help with a citation, don't hesitate to reach out.",
    touchHeading: "Get in Touch",
    touchBody:
      "The best way to contact us is by email. We aim to respond within 48 hours.",
    emailLine: "**Email:** [sam@sammysergi.com](mailto:sam@sammysergi.com)",
    commonHeading: "Common Questions",
    commonIntro:
      "Before reaching out, you may find the answer to your question in one of these resources:",
    resource1:
      "[Citation guides](/guides) — step-by-step instructions for citing sources in every major style",
    resource2:
      "[How-to-cite guides](/how-to-cite) — 750 source-type and style combinations with examples",
    resource3:
      "[Cheat sheets](/cheat-sheets) — quick reference for APA, MLA, and Chicago formatting",
    resource4:
      "[Writing tools](/tools) — word counter, readability checker, DOI validator, and more",
    issueHeading: "Report an Issue",
    issueIntro:
      "If you've found a citation formatting error, a broken feature, or an incorrect style, please include as much detail as possible when contacting us:",
    issue1: "The citation style you were using",
    issue2: "The source type (book, website, journal article, etc.)",
    issue3: "What you expected vs. what was generated",
    issue4: "Your browser and device (if relevant)",
    issueOutro: "This helps us identify and fix the issue quickly.",
    aboutHeading: "About CitationEasy",
    aboutBody:
      "CitationEasy is a free citation generator supporting over 10,000 citation styles. It was created by Samuel Sergi with the goal of making accurate citations accessible to everyone. Learn more on our [About page](/about).",
  },

  // /ai — the agent and developer docs.
  //
  // The code samples, endpoint paths and MCP tool NAMES are deliberately absent: they are
  // the API's identifiers, served in English by the API itself, and a translated one
  // would document an endpoint that does not exist.
  aiPage: {
    metaTitle: "CitationEasy for AI Agents - API, MCP Server & llms.txt",
    metaDescription:
      "Use CitationEasy programmatically: a free JSON API for citations in {count} styles, an MCP server for agents, llms.txt, an OpenAPI spec, and markdown views.",
    keyword1: "citation API",
    keyword2: "MCP server",
    keyword3: "citation generator API",
    keyword4: "llms.txt",
    keyword5: "OpenAPI",
    keyword6: "AI agent citations",
    keyword7: "free citation API",
    webApiDescription:
      "Free, unauthenticated JSON API that formats citations in {count} Citation Style Language styles from a URL, DOI, ISBN, PubMed ID, arXiv ID, or CSL-JSON metadata.",
    breadcrumb: "For AI & agents",
    heading: "CitationEasy for AI assistants and agents",
    intro:
      "Everything on this site is available to software as well as to people: a free JSON API, an MCP server, an OpenAPI spec, an llms.txt index, and a markdown view of all {count} pages. No key, no account, no rate limit of our own.",
    whyHeading: "Why call the API instead of writing the citation yourself",
    whyBody:
      "Citation formatting looks like a text-generation problem and isn't one. Every style encodes hundreds of rules about author inversion, initials, title case, container punctuation, edition statements, and date placement, and they differ in ways that are easy to get subtly wrong from memory. CitationEasy runs **citeproc-js** against the official Citation Style Language definitions — the same engine Zotero and Mendeley use — so output is deterministic and matches what a journal's submission system expects. Metadata comes from Crossref, Open Library, and PubMed; nothing is invented, and a field missing upstream stays missing rather than being filled in with a plausible guess.",
    generateHeading: "Generate a citation",
    generateBody:
      "Send `inputs` instead of `input` for up to 10 sources in one call, or `entry` with CSL-JSON metadata you already hold to skip the lookup entirely. Add `note: true` for the footnote form. Per-source failures come back on that source, so a batch never fails as a whole.",
    mcpHeading: "Connect over MCP",
    mcpIntro:
      "The Model Context Protocol endpoint at `/api/mcp` speaks JSON-RPC 2.0 over streamable HTTP and needs no credentials.",
    mcpToolsHeading: "Tools it exposes",
    endpointsHeading: "Endpoints",
    colEndpoint: "Endpoint",
    colWhat: "What it does",
    endpointCite:
      "Format up to 10 sources at once. Takes a URL, DOI, ISBN, PubMed ID, arXiv ID, BibTeX/RIS/CSL-JSON text, or a CSL-JSON entry object. Returns the reference entry, and optionally the in-text citation and footnote.",
    endpointStyles:
      "Search all {count} styles by name, citation-format class, discipline, or region.",
    endpointStyleSlug:
      "One style in full, with real formatted examples produced by its own CSL definition.",
    endpointSearch: "Relevance-ranked search across every page on the site.",
    endpointPages:
      "The complete site catalog as structured entries, filterable by page type.",
    endpointHowToCite:
      "Every source-type × style guidance page, filterable by either.",
    endpointTools: "Every free tool, with its category and keywords.",
    endpointGuides: "Every educational guide, with its category.",
    endpointConverters:
      "Every identifier and format conversion the site offers.",
    endpointComparisons:
      "Side-by-side style comparisons, linked to both styles' records.",
    endpointFields:
      'Academic fields, subject categories, and regions — for answering "which style should I use?".',
    endpointFaq:
      "Vetted answers to common citation questions, filterable with q.",
    endpointGlossary:
      "Citation and academic-writing terms with their full definitions — the response carries the text, so there is no page to fetch afterwards.",
    endpointIndex:
      "Endpoint discovery document. Start here if you are exploring.",
    discoveryHeading: "Discovery files",
    discoveryLlms: "Curated index of the site written for language models.",
    discoveryLlmsFull:
      "Every page the site publishes, grouped by type, one line each.",
    discoveryOpenapi: "OpenAPI 3.1 description of the whole public API.",
    discoveryMcp:
      "MCP endpoint (streamable HTTP). GET it in a browser for a description.",
    discoveryWellKnownMcp:
      "MCP discovery document for agents that probe a domain.",
    discoveryAiPlugin:
      "Legacy plugin manifest, for frameworks that still look for one.",
    discoveryMd: "Markdown view of any page on the site.",
    discoverySitemap: "Every indexable URL.",
    catalogHeading: "What is in the catalog",
    catalogIntro:
      "Every page below is reachable as HTML, as markdown under `/md/`, and as a structured entry from `/api/v1/pages`.",
    catalogStyles:
      "**{count}** citation styles, each with real examples generated from its own CSL definition",
    catalogTools: "**{count}** free browser tools",
    catalogGuides: "**{count}** educational guides",
    catalogHowToCite: "**{count}** how-to-cite pages",
    catalogComparisons: "**{count}** style comparisons",
    catalogConverters: "**{count}** converters",
    catalogFields: "**{count}** academic fields",
    catalogStyleLandings:
      "**{count}** major style hubs with full formatting rules",
    faqHeading: "Frequently Asked Questions",
    faqFreeQ: "Is the CitationEasy API free, and do I need an API key?",
    faqFreeA:
      "It is free and there is no API key. Every endpoint under /api/v1 is unauthenticated and CORS-open, so you can call it from a browser, a notebook, or an agent without registering. There is no rate limit of our own, but identifier lookups are proxied to Crossref, Open Library, and PubMed, which apply their own fair-use limits — batch with `inputs` instead of firing many parallel requests.",
    faqLlmQ: "Are the citations generated by a language model?",
    faqLlmA:
      "No. Formatting runs citeproc-js against the official Citation Style Language definitions, the same engine reference managers use. The same input and style always produce the same string. Bibliographic metadata comes from Crossref (DOIs), Open Library (ISBNs), and PubMed (PMIDs) — CitationEasy does not invent fields, so anything missing upstream is missing in the output rather than guessed.",
    faqMcpQ: "How do I connect an agent to CitationEasy over MCP?",
    faqMcpA:
      "Point any Model Context Protocol client at https://www.citationeasy.com/api/mcp using the streamable HTTP transport. No credentials are needed. In Claude Code: `claude mcp add --transport http citationeasy https://www.citationeasy.com/api/mcp`. The server exposes tools for generating citations, searching the {count} styles, source-type guidance, site search, and reading any page as markdown.",
    faqStylesQ: "Which citation styles can the API produce?",
    faqStylesA:
      'All {count} styles in the Citation Style Language repository, including APA 7, MLA 9, both Chicago systems, Harvard, Vancouver, IEEE, AMA, and thousands of individual journal styles. Pass the style slug in the `style` field. Friendly slugs like "chicago" and "harvard" resolve to the correct CSL variant, and /api/v1/styles?q= finds the rest.',
    faqLlmsTxtQ: "What is llms.txt and where is CitationEasy's?",
    faqLlmsTxtA:
      "llms.txt is a plain-text index of a site written for language models rather than browsers. CitationEasy's is at /llms.txt; the exhaustive version listing every one of the site's pages is at /llms-full.txt.",
    faqMarkdownQ: "Can I read CitationEasy pages as markdown instead of HTML?",
    faqMarkdownA:
      "Yes. Prefix any site path with /md/ — for example /md/citation-style/apa or /md/tools/word-counter. Data-driven pages return their full content, including the real formatted examples generated from each style's own CSL definition. Hand-written prose pages (guides, cheat sheets) return a summary card plus the canonical HTML URL, since their text lives in the page itself.",
    faqReuseQ: "May I use CitationEasy output in my own product?",
    faqReuseA:
      "Yes. A formatted citation is a fact about a source; use it freely. The CSL style definitions themselves come from the Citation Style Language project under CC BY-SA 3.0. Attribution back to CitationEasy is appreciated but not required.",
    missingHeading: "Something missing?",
    missingBody:
      "If your agent needs an endpoint, a field, or a format that isn't here, [tell us](/contact). Browsing by hand instead? [Start at the citation generator](/).",
  },

  // /quiz. The client component reads these as a prop rather than through a context:
  // it is one component, not the generator's nine, so there is nothing to drill through.
  quizPage: {
    metaTitle: "Which Citation Style Should I Use? - Free Style Quiz",
    metaDescription:
      "Answer a few quick questions about your field and paper type to find the best citation style for you: APA, MLA, Chicago, Vancouver, and more.",
    ogTitle: "Which Citation Style Should I Use?",
    ogDescription:
      "Take our free quiz to find the best citation style for your paper.",
    breadcrumbHome: "CitationEasy",
    breadcrumbCurrent: "Style Quiz",
    backLink: "← Back to Generator",
    heading: "Which Citation Style Should I Use?",
    intro:
      "Answer a few quick questions and we'll recommend the best citation style for your paper.",
    progress: "Question {current} of {total}",
    qDiscipline: "What is your field of study?",
    optSocialScience: "Psychology / Social Sciences",
    optHumanities: "Literature / Humanities",
    optHistory: "History / Art History",
    optMedical: "Medicine / Nursing / Health",
    optEngineering: "Engineering / Computer Science",
    optNaturalScience: "Natural Sciences (Biology, Chemistry)",
    optLaw: "Law",
    optBusiness: "Business / Economics",
    qPaperType: "What type of paper are you writing?",
    optResearch: "Research paper or thesis",
    optReview: "Literature review",
    optReport: "Lab report or technical report",
    optEssay: "Essay or class assignment",
    optJournal: "Journal article submission",
    qRegion: "Where is your institution located?",
    optUs: "United States",
    optUk: "United Kingdom / Australia",
    optCanada: "Canada",
    optEurope: "Europe (non-UK)",
    optOther: "Other",
    qPreference: "Do you have a preference for citation format?",
    optAuthorDate: "In-text citations (Author, Year)",
    optNumeric: "Numbered references [1]",
    optFootnotes: "Footnotes",
    optNoPreference: "No preference",
    styleVancouver: "Vancouver",
    styleAma: "AMA (American Medical Association)",
    styleIeee: "IEEE",
    styleBluebook: "Bluebook",
    styleCse: "CSE (Council of Science Editors)",
    styleChicagoNotes: "Chicago (Notes & Bibliography)",
    styleMla: "MLA (9th Edition)",
    styleApa: "APA (7th Edition)",
    styleHarvard: "Harvard",
    reasonVancouverMedical:
      "Vancouver is the standard for medical and health sciences journals, using numbered references.",
    reasonAma:
      "AMA is widely used in medical journals and uses numbered citations.",
    reasonIeee:
      "IEEE is the standard citation style for engineering, computer science, and IT fields.",
    reasonBluebook:
      "The Bluebook is the definitive citation style for legal writing. Chicago style is the closest general-purpose alternative.",
    reasonCse: "CSE is the standard for biology and natural sciences.",
    reasonChicagoHistory:
      "Chicago Notes & Bibliography is the gold standard for history and art history, using footnotes for citations.",
    reasonChicagoHumanities:
      "Chicago Notes & Bibliography works well for humanities with its footnote-based approach.",
    reasonMla:
      "MLA is the standard for literature, languages, and humanities fields.",
    reasonApaBusiness:
      "APA is widely used in business, economics, and management fields.",
    reasonHarvard:
      "Harvard referencing is the most common style in UK and Australian universities.",
    reasonChicagoFootnotes:
      "Chicago Notes & Bibliography uses footnotes, ideal for detailed citations.",
    reasonVancouverNumeric:
      "Vancouver uses numbered citations, keeping your text clean and readable.",
    reasonApaDefault:
      "APA is the most widely used citation style across social sciences, education, and many other disciplines.",
    recommendLabel: "We recommend",
    generateButton: "Generate {style} Citations",
    retakeButton: "Take Quiz Again",
    guidesLink: "Browse our citation guides for more help →",
    previousButton: "← Previous question",
  },

  // /search — the server-rendered site search.
  searchPage: {
    metaTitle: "Search CitationEasy",
    metaDescription:
      "Search every page on CitationEasy at once: {count} citation styles, 250+ free tools, guides, how-to-cite pages, style comparisons, converters, and the glossary.",
    keyword1: "search citation styles",
    keyword2: "find citation guide",
    keyword3: "citation tool search",
    ogDescription:
      "Search every page on the site at once — styles, tools, guides, how-to-cite pages, comparisons, converters, and glossary terms.",
    ogImageSubtitle:
      "Every citation style, tool, guide, how-to page, comparison, converter, and glossary term — searched together.",
    ogKind: "Search",
    breadcrumb: "Search",
    heading: "Search CitationEasy",
    intro:
      "Every citation style, tool, guide, how-to page, comparison, converter, and glossary term on the site — searched together.",
    inputLabel: "Search query",
    inputPlaceholder: "e.g. cite a podcast in APA, hanging indent, DOI",
    submit: "Search",
    browseHeading: "Or browse by section",
    browseStyles: "Citation styles",
    browseStylesNote: "{count} styles",
    browseTools: "Free tools",
    browseToolsNote: "{count} tools",
    browseHowTo: "How to cite any source",
    browseHowToNote: "{count} pages",
    browseGuides: "Guides",
    browseGuidesNote: "{count} guides",
    browseGlossary: "Glossary",
    browseGlossaryNote: "{count} terms",
    browseComparisons: "Style comparisons",
    browseComparisonsNote: "{count} comparisons",
    browseConverters: "Converters",
    browseConvertersNote: "{count} converters",
    browseCheatSheets: "Cheat sheets",
    browseCheatSheetsNote: "{count} sheets",
    facetsLabel: "Filter by page type",
    facetAll: "All ({count})",
    noMatches: "No pages match “{query}”.",
    matchesOne: "{count} page match “{query}”.",
    matchesMany: "{count} pages match “{query}”.",
    pagerLabel: "Result pages",
    pagerPrevious: "← Previous",
    pagerNext: "Next →",
    pagerPosition: "Page {page} of {total}",
    paletteLoading: "Searching…",
    paletteError: "Search is unavailable right now. Please try again.",
    paletteViewAll: "View all results for “{query}”",
    apiNote:
      "Building something? The same index is available as JSON at [/api/v1/search](/api/v1/search?q=podcast) and over MCP — see [the agent docs](/ai).",
  },

  // /cite-this-page. The six FAQ answers used to exist twice — once in the layout for the
  // FAQPage markup and once in the page for the visible list — which is exactly the drift
  // this dictionary removes: both now read the same keys.
  citeThisPage: {
    metaTitle: "Cite This Page - Free Bookmarklet & Chrome Extension",
    metaDescription:
      "Cite any web page in one click with the free Cite This Page bookmarklet or Chrome extension. Works on Chromebooks and locked-down browsers too.",
    ogTitle: "Cite This Page Bookmarklet & Chrome Extension",
    ogDescription:
      "One-click website citations from any page — a drag-and-drop bookmarklet plus a free Chrome extension with right-click citing.",
    breadcrumbRoot: "CitationEasy",
    breadcrumbSchemaCurrent: "Cite This Page Bookmarklet",
    appName: "Cite This Page Bookmarklet",
    appDescription:
      "A free bookmarklet that cites the web page you are reading in one click — it opens CitationEasy with the page's URL prefilled, then formats the citation in APA, MLA, Chicago, and {count} more styles.",
    breadcrumbLabel: "Breadcrumb",
    breadcrumbHome: "Home",
    breadcrumbCurrent: "Cite This Page Bookmarklet",
    heading: "Cite This Page: Bookmarklet & Chrome Extension",
    intro:
      "Cite any web page in one click — no sign-up. Drag the bookmarklet below to your bookmarks bar (no install at all), or grab the free Chrome extension for a toolbar button and right-click citing.",
    dragPrompt: "Drag this button to your bookmarks bar:",
    buttonTitle: "Drag me to your bookmarks bar",
    buttonLabel: "📑 Cite This Page",
    dontClick: "Don't click it here — drag it up to your bookmarks bar first.",
    installHeading: "How to install it",
    step1Title: "Show your bookmarks bar",
    step1Body:
      "In most browsers press Ctrl+Shift+B (Cmd+Shift+B on Mac) to toggle the bookmarks bar so you have somewhere to drop the button.",
    step2Title: "Drag the button up",
    step2Body:
      "Click and hold the 'Cite This Page' button below, then drag it onto your bookmarks bar and release. It becomes a normal bookmark.",
    step3Title: "Click it on any page",
    step3Body:
      "While reading an article, news story, or journal page, click your new bookmark. CitationEasy opens with that page's URL prefilled and the citation ready to copy.",
    manualHeading: "Can't drag it? Add it manually",
    manualBody:
      'Create a new bookmark, name it "Cite This Page", and paste this as the URL/address:',
    howHeading: "How it works",
    howBody:
      "The bookmarklet runs no code on the page you are reading. It only hands that page's web address to CitationEasy, where our server fetches and parses the public page metadata — the same trusted path used when you paste a URL into the generator by hand. That keeps it safe and avoids the cross-origin limits that retired older in-page bookmarklets.",
    extensionHeading: "Prefer a Chrome extension?",
    extensionBody:
      "The free CitationEasy extension adds a toolbar button that cites the current tab, plus right-click options to cite a link — or a selected DOI or ISBN — without opening it. Like the bookmarklet, it runs no code on the pages you visit: it only hands the URL to CitationEasy. It needs just the `activeTab` and `contextMenus` permissions, and works in Chrome, Edge, Brave, and other Chromium browsers.",
    downloadButton: "⬇ Download the Chrome extension (.zip)",
    install1: "Download and unzip the extension.",
    install2:
      "Open `chrome://extensions` and turn on **Developer mode** (top right).",
    install3: "Click **Load unpacked** and select the unzipped folder.",
    install4:
      "Pin the CitationEasy button, then click it on any page to cite it.",
    faqHeading: "Frequently Asked Questions",
    faqWhatQ: "What is the 'Cite This Page' bookmarklet?",
    faqWhatA:
      "It is a small bookmark you drag to your browser's bookmarks bar. When you click it on any web page, it opens CitationEasy in a new tab with that page's URL already filled in, so you get a formatted citation in one click — no copy-paste.",
    faqExtensionNeededQ: "Do I need to install an extension?",
    faqExtensionNeededA:
      "No. A bookmarklet is just a bookmark, so there is nothing to install and no permissions to grant. It works on locked-down school Chromebooks and most mobile browsers where extensions are blocked. If you prefer an extension, a free Chrome extension is also available on this page.",
    faqChromeQ: "Is there a Chrome extension?",
    faqChromeA:
      "Yes. The free CitationEasy Chrome extension adds a toolbar button that cites the current tab, plus right-click options to cite a link or a selected DOI/ISBN without opening it. It runs no code on the pages you visit and needs only the activeTab and contextMenus permissions. Download it from this page and load it via chrome://extensions, and it works in Chrome, Edge, Brave, and other Chromium browsers.",
    faqAddQ: "How do I add the bookmarklet?",
    faqAddA:
      "Make sure your bookmarks bar is visible, then drag the 'Cite This Page' button on this page onto it. If dragging is not possible (for example on mobile), create a new bookmark manually and paste the bookmarklet code as the URL.",
    faqSafeQ: "Is it safe? Does it read the page I'm on?",
    faqSafeA:
      "Yes, it is safe. The bookmarklet runs no code on the page you are reading — it only sends that page's web address to CitationEasy, where our server fetches and parses the public page metadata, exactly as it does when you paste a URL manually.",
    faqOtherSourcesQ: "Does it work for sources other than websites?",
    faqOtherSourcesA:
      "The bookmarklet is for web pages, but the underlying link also accepts a DOI, ISBN, PMID, or arXiv ID via the ?source= parameter — so a shareable 'cite this' link works for journal articles and books too.",
    relatedHeading: "Related Tools",
    relatedGenerator: "Citation Generator",
    relatedHowToCite: "How to Cite a Website",
    relatedTools: "All Tools",
  },

  // /shared-bibliography — opens a bibliography that travelled inside a share link.
  sharedBibliographyPage: {
    metaTitle: "Shared Bibliography - View & Import Shared Citations",
    metaDescription:
      "Open a bibliography someone shared from CitationEasy. View the formatted citations, copy them, or import the whole list. Free, no account needed.",
    ogTitle: "Shared Bibliography",
    ogDescription:
      "View and import a shared bibliography. The citations travel inside the link — nothing is stored on a server, and links never expire.",
    breadcrumbRoot: "CitationEasy",
    breadcrumbLabel: "Breadcrumb",
    breadcrumbHome: "Home",
    breadcrumbCurrent: "Shared Bibliography",
    heading: "Shared Bibliography",
    intro:
      "Someone shared these citations with you. Copy them, or import the whole list into your own CitationEasy bibliography with one click.",
    loading: "Opening the shared bibliography…",
    copyAll: "Copy all",
    copied: "Copied",
    copyFailed: "Copy failed",
    citationOne: "citation",
    citationMany: "citations",
    addAll: "Add all to my bibliography",
    added: "{count} added to your bibliography",
    skippedOne: "{count} duplicate skipped",
    skippedMany: "{count} duplicates skipped",
    openGenerator: "open the generator",
    emptyHeading: "No shared bibliography in this link",
    emptyBody:
      "This page displays bibliographies shared from CitationEasy. To share yours, open your bibliography in the [citation generator](/), choose **Export → Copy share link**, and send the link to anyone. The citations travel inside the link itself — nothing is stored on a server, so links keep working forever and your data stays private.",
    decodeError: "This share link could not be opened.",
  },

  // The 19 source types the 15 detailed styles show worked examples for
  // (`DetailedStyleData.examples[].sourceType`). 75 rows, 19 distinct values — so this
  // is a vocabulary, not per-page copy, and it belongs here rather than repeated in
  // every locale's `src/content/style-pages/<locale>.json`.
  //
  // Two casings for the same reason `howToCitePage.refList*Lower` has two: the pages
  // print the label as a heading ("Book") and inside a sentence ("a sample APA entry
  // for a book"). Lower-casing the translated noun in code is wrong in German, where
  // every one of these is capitalised, so the lower form is a real key a translator
  // fills rather than a `.toLowerCase()` call.
  //
  // The citations themselves — `referenceEntry` and `inTextCitation` — are NOT here and
  // are not translatable anywhere: they are worked examples in a specific style's own
  // punctuation. See `src/lib/localized-content.ts`.
  sourceTypes: {
    book: "Book",
    bookLower: "book",
    bookChapter: "Book Chapter",
    bookChapterLower: "book chapter",
    case: "Case",
    caseLower: "case",
    conferencePaper: "Conference Paper",
    conferencePaperLower: "conference paper",
    courtCase: "Court Case",
    courtCaseLower: "court case",
    federalStatute: "Federal Statute",
    federalStatuteLower: "federal statute",
    governmentDocument: "Government Document",
    governmentDocumentLower: "government document",
    governmentReport: "Government Report",
    governmentReportLower: "government report",
    journalArticle: "Journal Article",
    journalArticleLower: "journal article",
    lawReviewArticle: "Law Review Article",
    lawReviewArticleLower: "law review article",
    legislation: "Legislation",
    legislationLower: "legislation",
    newspaperArticle: "Newspaper Article",
    newspaperArticleLower: "newspaper article",
    patent: "Patent",
    patentLower: "patent",
    podcast: "Podcast",
    podcastLower: "podcast",
    preprint: "Preprint",
    preprintLower: "preprint",
    standard: "Standard",
    standardLower: "standard",
    thesis: "Thesis",
    thesisLower: "thesis",
    website: "Website",
    websiteLower: "website",
    youtubeVideo: "YouTube Video",
    youtubeVideoLower: "youtube video",
  },

  // Chrome shared by the five per-style guide routes — /examples, /format,
  // /in-text-citation, /reference-page and /annotated-bibliography, 15 styles each.
  //
  // One section rather than five copies: the CTA, the back link and the cross-links
  // between the five pages are the same sentences on every one of them, and duplicating
  // them per route would be five identical translation units instead of one.
  styleGuidePages: {
    backToStyle: "Back to {style}",
    backToGenerator: "Back to Citation Generator",
    ctaHeading: "Generate {style} Citations Automatically",
    ctaButton: "Cite in {style} Now →",
    faqHeading: "Frequently Asked Questions",
    resourcesHeading: "More Resources",
    relatedHeading: "Related {style} Guides",
    resourceGenerator: "Free Citation Generator ({count} styles)",
    resourceTools: "Free Writing Tools",
    resourceGuides: "All Citation Guides",
    resourceStyleGenerator: "{style} Citation Generator",
    resourceExamples: "{style} Citation Examples",
    resourceFormat: "{style} Format Guide",
    resourceInText: "{style} In-Text Citation Guide",
    resourceAnnotatedBib: "{style} Annotated Bibliography Guide",
    resourceReferencePage: "{style} {list} Guide",
    notFoundTitle: "Not Found",
  },

  // /examples/<style> — the copy-ready worked examples for all 15 detailed styles.
  examplesPage: {
    metaTitle: "{style} Citation Examples - All Source Types [2026]",
    metaDescription:
      "View formatted {style} ({edition}) citation examples for books, journal articles, websites, and more. Copy-ready {list} entries and in-text citations.",
    ogTitle: "{style} citation examples",
    ogDescription: "Copy-ready {style} entries for every common source type.",
    ogImageTitle: "{style} Citation Examples",
    ogKind: "{style} {edition}",
    keywordExamples: "{style} citation examples",
    keywordReference: "{style} reference examples",
    keywordInText: "{style} in-text citation examples",
    keywordList: "{style} {listLower} examples",
    keywordHowTo: "how to cite in {style}",
    breadcrumb: "{style} Citation Examples",
    heading: "{style} Citation Examples",
    lead: "View correctly formatted {style} ({edition}) citation examples for {count} common source types. Each example includes both the {listLower} entry and the corresponding in-text citation.",
    entryLabel: "{list} Entry:",
    inTextLabel: "In-Text Citation:",
    ctaBody:
      "Skip the manual formatting. Paste a DOI, URL, or ISBN into CitationEasy and get a perfectly formatted {style} citation in seconds.",
    faqLookQ: "What does an {style} citation look like?",
    faqLookA:
      "An {style} citation includes a {listLower} entry with full source details and an in-text citation. The format varies by source type. For example, a book citation in {style} ({edition}) includes the author, year, title, and publisher.",
    faqHowManyQ: "How many {style} citation examples are available?",
    faqHowManyA:
      "CitationEasy provides {style} citation examples for {count} source types including {types}. Each example includes both the {listLower} entry and the corresponding in-text citation.",
    faqCopyQ: "Can I copy these {style} citation examples?",
    faqCopyA:
      "Yes, the {style} examples on this page show the exact formatting for each source type. You can use them as templates for your own citations. For automatic formatting, use our free {style} citation generator.",
  },

  // /format/<style> — font, margins, spacing, title page and headers. The VALUES those
  // labels take are per-style prose and live in the overlay, not here.
  formatPage: {
    metaTitle: "{style} Format Guide - Paper Formatting Rules [2026]",
    metaDescription:
      "Complete {style} ({edition}) paper formatting guide. Learn the correct font, margins, spacing, title page, headers, and {listLower} formatting rules.",
    ogTitle: "{style} paper format",
    ogDescription:
      "Margins, font, spacing, and title page for a {style} paper.",
    ogImageTitle: "{style} Paper Format",
    ogImageSubtitle: "{margins} margins · {spacing}",
    ogKind: "{style} {edition}",
    keywordFormat: "{style} format",
    keywordPaperFormat: "{style} paper format",
    keywordRules: "{style} formatting rules",
    keywordMargins: "{style} margins font spacing",
    keywordList: "{style} {listLower} format",
    breadcrumb: "{style} Format Guide",
    heading: "{style} Format Guide",
    lead: "Follow these {style} ({edition}) paper formatting rules to ensure your document meets the required standards. This guide covers font, margins, spacing, title page, headers, and {listLower} formatting.",
    paperHeading: "Paper Formatting",
    paperIntro:
      "Below are the core formatting requirements for papers written in {style} ({edition}) style.",
    labelFont: "Font",
    labelFontSize: "Font Size",
    labelMargins: "Margins",
    labelSpacing: "Spacing",
    labelTitlePage: "Title Page",
    labelHeaders: "Headers / Page Numbers",
    listHeading: "{list} Formatting",
    listIntro:
      "Your {listLower} must follow these rules to be correctly formatted in {style} style:",
    ctaBody:
      "Let CitationEasy handle the formatting. Paste a DOI, URL, or ISBN and get a perfectly formatted {style} citation in seconds.",
    faqFontQ: "What font and size should I use for {style} format?",
    faqFontA:
      "{style} ({edition}) requires {font}. The standard font size is {fontSize}.",
    faqMarginsQ: "What are the margin requirements for {style}?",
    faqMarginsA:
      "{style} papers require {margins}. This applies to the top, bottom, left, and right margins of every page.",
    faqTitlePageQ: "Does {style} require a title page?",
  },

  // /in-text-citation/<style> — the six in-text forms. The forms themselves
  // ("(Smith, 2024)") are per-style prose in the overlay.
  inTextCitationPage: {
    metaTitle: "{style} In-Text Citation Guide - Rules & Examples [2026]",
    metaDescription:
      "Learn how to format {style} ({edition}) in-text citations. Rules for single author, multiple authors, direct quotes, no author, and no date with examples.",
    ogTitle: "{style} in-text citations",
    ogDescription: "Every {style} in-text citation form, with examples.",
    ogImageTitle: "{style} In-Text Citations",
    ogImageSubtitle: "One author {singleAuthor} · three or more {threeOrMore}",
    ogKind: "{style} {edition}",
    keywordInText: "{style} in-text citation",
    keywordExamples: "{style} in-text citation examples",
    keywordParenthetical: "{style} parenthetical citation",
    keywordRules: "{style} citation rules",
    keywordHowTo: "how to cite in text {style}",
    breadcrumb: "{style} In-Text Citations",
    heading: "{style} In-Text Citations",
    lead: "Master {style} ({edition}) in-text citations with this complete guide. Learn the correct format for every scenario, including single and multiple authors, direct quotes, and sources with missing information.",
    rulesHeading: "In-Text Citation Rules",
    rulesIntro:
      "Below are the {style} in-text citation rules for every common scenario you will encounter when writing your paper.",
    labelSingleAuthor: "Single Author",
    labelTwoAuthors: "Two Authors",
    labelThreeOrMore: "Three or More Authors",
    labelDirectQuote: "Direct Quote",
    labelNoAuthor: "No Author",
    labelNoDate: "No Date",
    examplesHeading: "Quick Examples",
    examplesIntro:
      "Here are real-world {style} in-text citation examples for different source types:",
    ctaBody:
      "Stop worrying about in-text citation formatting. Paste a DOI, URL, or ISBN into CitationEasy and get both the {listLower} entry and in-text citation instantly.",
    faqFormatQ: "How do I format an {style} in-text citation?",
    faqFormatA:
      "For a single author in {style}, use the format {singleAuthor}. For two authors, use {twoAuthors}. For three or more authors, use {threeOrMore}.",
    faqQuoteQ: "How do I cite a direct quote in {style}?",
    faqQuoteA:
      "For direct quotes in {style} ({edition}), use the format: {directQuote}.",
    faqMissingQ: "What if there is no author or no date in {style}?",
    faqMissingA:
      "When there is no author in {style}, use: {noAuthor}. When there is no date, use: {noDate}.",
  },

  // /reference-page/<style> — how to build the list at the back of the paper.
  //
  // The five `mistake*` strings carry `**bold**` inline markup because the lead-in of
  // each bullet is emphasised inside the sentence; they render through `InlineText`.
  referenceListPage: {
    metaTitle: "{style} {list} Guide - Formatting Rules [2026]",
    metaDescription:
      "Learn how to format a {style} {listLower} ({edition}). Step-by-step formatting rules, examples, and common mistakes to avoid for {fullName}.",
    ogTitle: "{style} {listLower}",
    ogDescription: "How to build the {list} in {style}.",
    ogImageTitle: "{style} {list}",
    ogKind: "{style} {edition}",
    breadcrumb: "How to Format a {style} {list}",
    heading: "How to Format a {style} {list}",
    lead: "The {listLower} is one of the most important parts of any academic paper formatted in {style} ({edition}). It provides readers with the full details of every source you cited, allowing them to locate and verify your references. Getting the formatting right is essential for academic credibility. This guide covers the exact rules, shows real examples, and highlights the most common mistakes students make.",
    rulesHeading: "Formatting Rules",
    rulesIntro: "Follow these rules when creating your {style} {listLower}:",
    exampleHeading: "Example {list}",
    exampleIntro:
      "Here is how a {style} {listLower} looks with entries for different source types:",
    mistakesHeading: "Common Mistakes to Avoid",
    mistakesIntro:
      "These are the five most frequent formatting errors students make when creating a {style} {listLower}:",
    mistakeIndent:
      "**Missing or incorrect hanging indent:** Every entry should have the first line flush left and all subsequent lines indented by 0.5 inches. Use your word processor's paragraph settings rather than pressing the Tab key.",
    mistakeOrder:
      '**Incorrect alphabetization or ordering:** Entries must be arranged according to {style} rules. Ignore articles like "A," "An," and "The" when alphabetizing. For numbered styles, list entries in the order they first appear in the text.',
    mistakeSpacing:
      "**Inconsistent spacing:** Double-check your line spacing throughout the entire {listLower}. Mixing single and double spacing is a common error that is easy to miss.",
    mistakeItalics:
      "**Wrong use of italics:** Italicize titles of standalone works such as books and journals, but do not italicize article or chapter titles. Check {style} guidelines for exactly which elements should be italicized.",
    mistakeLinks:
      "**Missing or broken DOIs and URLs:** Always include a DOI when one is available, formatted as a clickable link. Test every URL before submitting your paper to make sure it leads to the correct source.",
    ctaHeading: "Generate Your {list} Automatically",
    ctaBody:
      "Skip the manual formatting. Paste a DOI, URL, or ISBN into CitationEasy and get a perfectly formatted {style} citation in seconds, ready for your {listLower}.",
    faqWhatQ: "What is a {style} {list}?",
    faqWhatA:
      "A {style} {list} is the section at the end of your paper that lists all the sources you cited in your text, formatted according to {fullName} ({edition}) guidelines. It allows readers to locate and verify each source you referenced.",
    faqFormatQ: "How do I format a {style} {listLower}?",
    faqFormatA:
      'Start on a new page, title it "{list}," and list all sources you cited in your paper. Follow the specific {style} rules for hanging indents, spacing, alphabetization or numbering order, and italics.',
    faqDifferenceQ:
      "What is the difference between a {listLower} and a bibliography?",
    faqDifferenceA:
      'A {listLower} includes only the sources you directly cited in your paper, while a bibliography may also include sources you consulted but did not cite. In {style} ({edition}), the end-of-paper list is called the "{list}."',
  },

  // /annotated-bibliography/<style>. `whatTypes` carries `**bold**` inline markup.
  //
  // `exampleAnnotation` is a specimen annotation, not a citation: it is ordinary prose
  // about an imaginary source and translates like any other paragraph. The reference
  // entry printed above it does not — that comes from `DETAILED_STYLES` and stays as
  // the style wrote it.
  annotatedBibPage: {
    metaTitle: "{style} Annotated Bibliography Guide & Examples [2026]",
    metaDescription:
      "How to write an annotated bibliography in {style} ({edition}): formatting rules, example entries, and tips for getting every annotation right.",
    ogTitle: "{style} annotated bibliography",
    ogDescription: "How to format an annotated bibliography in {style}.",
    ogImageTitle: "{style} Annotated Bibliography",
    ogKind: "{style} {edition}",
    breadcrumb: "{style} Annotated Bibliography",
    heading: "{style} Annotated Bibliography",
    lead: "An annotated bibliography is a list of source citations that each include a short descriptive or evaluative paragraph. In {style} format ({edition}), each entry begins with a properly formatted reference followed by an annotation that summarizes, assesses, or reflects on the source. This guide walks you through the rules, formatting, and examples you need to create a polished {style} annotated bibliography.",
    whatHeading: "What Is an Annotated Bibliography?",
    whatBody:
      "An annotated bibliography goes beyond a standard {listLower} by adding a brief paragraph after each citation. This annotation gives readers insight into the content, quality, and relevance of the source. Instructors often assign annotated bibliographies to help students evaluate their sources critically and demonstrate how each source contributes to their research topic.",
    whatTypes:
      "There are three common types of annotations: **descriptive** annotations that summarize the source, **evaluative** annotations that assess the quality and reliability of the work, and **combination** annotations that do both. Your assignment instructions should specify which type to use.",
    rulesHeading: "{style} Annotated Bibliography Rules",
    rulesIntro:
      "Follow these rules when formatting an annotated bibliography in {fullName}:",
    exampleHeading: "Example Annotated Bibliography Entry",
    exampleIntro:
      "Below is a sample {style} annotated bibliography entry for a {sourceType}:",
    exampleAnnotation:
      "This source provides a comprehensive overview of the topic, presenting well-supported arguments backed by empirical evidence. The author draws on a wide range of primary and secondary sources to build a persuasive case. The methodology is rigorous and the findings are clearly presented, making this work a valuable resource for understanding the subject. This source is particularly useful for establishing the theoretical framework of the research project and offers perspectives that complement other works in the bibliography.",
    tipsHeading: "Tips for Writing {style} Annotations",
    tipSummarize:
      "Start by summarizing the source in two to three sentences, then add your evaluation of its strengths, limitations, and relevance.",
    tipTone:
      "Use third person and an academic tone throughout your annotations. Avoid casual language or first-person reflections unless your instructor requests them.",
    tipLength:
      "Keep each annotation between 100 and 200 words. Be concise and focus on the most important aspects of the source.",
    tipRelevance:
      "Explain how the source fits into your overall research. Describe what unique perspective or evidence it contributes.",
    tipCitation:
      "Make sure the citation portion of each entry follows {fullName} formatting rules exactly before writing the annotation.",
    ctaHeading: "Build Your Annotated Bibliography Automatically",
    ctaBody:
      "Use the CitationEasy Annotated Bibliography Maker to generate properly formatted {style} citations, then add your annotations.",
    ctaButton: "Open Annotated Bibliography Maker →",
    faqWhatQ: "What is an annotated bibliography in {style}?",
    faqWhatA:
      "An annotated bibliography in {style} ({edition}) is a list of citations formatted in {fullName} style, each followed by a brief paragraph that summarizes, evaluates, or reflects on the source. It helps readers understand the relevance and quality of each source.",
    faqLengthQ: "How long should annotations be?",
    faqLengthA:
      "Annotations are typically 100 to 200 words. Some assignments may require shorter or longer annotations depending on the depth of analysis expected. Always check your instructor's guidelines for specific length requirements.",
    faqIncludeQ: "What should an annotation include?",
    faqIncludeA:
      "An annotation should include a brief summary of the source's main argument or findings, an evaluation of the source's credibility and usefulness, and a reflection on how the source fits into your research. Some annotations are purely descriptive, while others include critical analysis.",
  },

  guidePage: {
    byline: "By {author}",
    published: "Published {date}",
    updated: "Updated {date}",
    readingTime: "one:{count} minute to read|other:{count} minutes to read",
    wordCount: "one:{count} word|other:{count} words",
  },

  // /guides hub, /guides/<category> index pages, and the chrome around each guide
  guideLibrary: {
    eyebrow: "Guide library",
    browseByCategory: "Browse by category",
    recentlyUpdated: "Recently updated",
    allGuides: "All guides",
    guideCount: "one:{count} guide|other:{count} guides",
    updatedOn: "Updated {date}",
    minRead: "one:{count} min read|other:{count} min read",
    onThisPage: "On this page",
    viewAllInCategory: "All {category} guides",
    moreInCategory: "More in {category}",
    aboutThisGuide: "About this guide",
    provenance:
      "Written by {author}. Published {published}; last updated {updated}.",
    categoryIntroSuffix:
      "Every guide is free, written for students and researchers, and dated so you can see when it was last checked.",
    photoCredit: "Photo: {author}, {source} ({license})",
    imageAlt: "Photograph illustrating the {category} guides",
    imageSourceCommons: "Wikimedia Commons",
    apaStyleName: "APA Style",
    apaStyleDescription:
      "How to cite websites, journal articles, books, videos, and more in APA 7th edition, with reference-list and in-text examples for each.",
    mlaStyleName: "MLA Style",
    mlaStyleDescription:
      "How to cite every common source in MLA 9th edition, from web pages and journal articles to poems, plays, films, and course materials.",
    chicagoStyleName: "Chicago Style",
    chicagoStyleDescription:
      "Chicago Manual of Style guides covering notes-bibliography and author-date citations for books, articles, archives, government documents, and media.",
    styleComparisonsName: "Style Comparisons",
    styleComparisonsDescription:
      "Side-by-side comparisons of APA, MLA, Chicago, Harvard, Vancouver, IEEE, and more, plus advice on which style your discipline expects.",
    researchMethodsName: "Research Methods",
    researchMethodsDescription:
      "How to plan a research paper, review the literature, take notes, evaluate sources, avoid plagiarism, and build a bibliography.",
    academicWritingName: "Academic Writing",
    academicWritingDescription:
      "Writing guides for abstracts, introductions, conclusions, thesis statements, methodology sections, and clear academic prose.",
    fieldSpecificName: "Field-Specific",
    fieldSpecificDescription:
      "Citation guides for individual disciplines, from psychology, history, and literature to medicine, engineering, law, and nursing.",
    sourceEvaluationName: "Source Evaluation",
    sourceEvaluationDescription:
      "How to judge credibility, tell scholarly from popular sources, use Google Scholar and library databases, and spot bias.",
    citationHelpName: "Citation Help",
    citationHelpDescription:
      "Answers to the tricky cases: missing authors or dates, multiple authors, PDFs, AI tools, legal cases, and importing or exporting citations.",
  },

  common: {
    backToGuides: "← Back to Guides",
    free: "Free",
    noSignup: "No sign-up required",
    backToHome: "Back to the citation generator",
    inEnglish: "In English",
    viewInEnglish: "View in English",
    availableInEnglish: "This page is available in English with more detail.",
    otherLanguages: "Other languages",
    skipToContent: "Skip to content",
    cookieNotice:
      "We use cookies for analytics and advertising to keep CitationEasy free.",
    privacyPolicy: "Privacy Policy",
    accept: "Accept",
    decline: "Decline",
    advertisement: "Advertisement",
  },
  legalPage: {
    backLink: "← Back to Generator",
    effective: "Effective Date:",
    updated: "Last Updated:",
  },
} as const;

/**
 * The shape every locale must satisfy. Because it is derived from `en`, adding a key
 * here breaks the build until every language supplies it.
 */
export type Dictionary = {
  [Section in keyof typeof en]: { [Key in keyof (typeof en)[Section]]: string };
};

export default en as Dictionary;
